/**
 * BuildingDetail page - displays building details and character assignment UI.
 * Uses HexLayout for consistent navigation with the hex view page.
 * Features a paper-like parchment aesthetic similar to the Missives page.
 */

import { Link, useForm, router } from '@inertiajs/react';
import type { HexBuilding, AssignedCharacter, BuildingRecipe } from '@/types/buildings';
import type { ResourceData } from '@/components/HexResources';
import { getRankColorClass, formatDuration } from '@/types/buildings';
import { RESOURCE_LABELS, UserResources } from '@/types/crafting';
import { StarRating } from '@/components/Buildings/StarRating';
import { BuildingProgressBar } from '@/components/Buildings/BuildingProgressBar';
import { HexResources } from '@/components/HexResources';
import { HexQuickLinks } from '@/components/HexQuickLinks';
import { useState, useEffect, useCallback } from 'react';
import HexLayout from '@/layouts/HexLayout';
import { CornerFlourish, ParchmentDivider, CoffeeStain } from '@/components/Parchment';
import {
    PortalMissiveInfo,
    CampHallRecruitmentInfo,
    LumberMillInfo,
    HerbalistHutInfo,
    TeaHouseInfo,
    ShrineInfo,
    SmokehouseInfo,
    StorageShedInfo,
} from '@/components/Buildings/Info';

interface AvailableCharacter {
    id: number;
    name: string;
    class: {
        value: string;
        label: string;
    };
    hex_id: number;
}

interface Props {
    hex: {
        id: number;
        q: number;
        r: number;
        name: string;
        terrain: string;
        owner: {
            id: number;
            username: string;
        };
        collaborators: Array<{ id: number; username: string }>;
        resources: ResourceData;
    };
    building: HexBuilding;
    nextLevelCost: ResourceData | null;
    nextStarCost: ResourceData | null;
    nextLevelTime: number | null;
    nextStarTime: number | null;
    isOwner: boolean;
    availableCharacters: AvailableCharacter[];
    // Recipes available for crafting with this building
    buildingRecipes: BuildingRecipe[];
    // Conservatory-specific production data
    conservatoryInfo: {
        level: number;
        stars: number;
        level_production_amount: number;
        production_hours: number;
        is_producing: boolean;
        can_start_production: boolean;
        current_resource: string | null;
        started_at: string | null;
        completes_at: string | null;
        is_complete: boolean;
        time_remaining_minutes: number | null;
        production_amount: number | null;
    } | null;
    // Moth House-specific training data
    mothHouseInfo: {
        level: number;
        stars: number;
        xp_amount: number;
        max_character_level: number;
        is_training: boolean;
        can_start_training: boolean;
        training_character: {
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string } | null;
        } | null;
        started_at: string | null;
        completes_at: string | null;
        is_complete: boolean;
        time_remaining_minutes: number | null;
        eligible_characters: Array<{
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string } | null;
        }>;
    } | null;
    // Mystical Focus-specific item processing data
    mysticalFocusInfo: {
        level: number;
        stars: number;
        effect_power: number;
        base_hours: number;
        character_reduction: number;
        processing_hours: number;
        is_processing: boolean;
        can_start_action: boolean;
        processing_item: {
            id: number;
            name: string;
            recipe: { name: string; image_path: string | null } | null;
            quality: number;
            stats: Record<string, { value: number; label: string }>;
        } | null;
        action: 'upgrade' | 'reroll' | null;
        upgrade_stat: string | null;
        started_at: string | null;
        completes_at: string | null;
        is_complete: boolean;
        time_remaining_minutes: number | null;
        eligible_items: Array<{
            id: number;
            name: string;
            recipe: { name: string; image_path: string | null } | null;
            quality: number;
            stats: Record<string, { value: number; label: string }>;
        }>;
        assigned_characters: Array<{
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string | null } | null;
        }>;
        stat_labels: Record<string, string>;
    } | null;
    // Tool Bench-specific equipment slot and crafting bonus data
    toolBenchInfo: {
        level: number;
        stars: number;
        equipment_slot_limit: number;
        equipment_slot_characters: Array<{
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string } | null;
        }>;
        eligible_characters: Array<{
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string } | null;
        }>;
        available_equipment_slots: number;
        crafting_bonus_chance: number;
        crafting_stat_bonus: number;
        assigned_count: number;
    } | null;
    // Apothecary-specific healing data
    apothecaryInfo: {
        level: number;
        stars: number;
        base_healing_duration_minutes: number;
        healing_duration_minutes: number;
        time_reduction_percent: number;
        passive_body_bonus: number;
        passive_body_bonus_from_level: number;
        passive_body_bonus_from_workers: number;
        slot_count: number;
        available_slots: number;
        worker_count: number;
        can_accept_workers: boolean;
        patient_slot_count: number;
        available_patient_slots: number;
        patient_count: number;
        can_accept_patients: boolean;
        workers: Array<{
            id: number;
            name: string;
            slot: number;
            class: { name: string; icon: string | null } | null;
        }>;
        healing_characters: Array<{
            id: number;
            name: string;
            slot: number;
            current_health: number;
            max_health: number;
            health_percentage: number;
            is_incapacitated: boolean;
            started_at: string | null;
            completes_at: string | null;
            is_complete: boolean;
            time_remaining_minutes: number | null;
            class: { name: string; icon: string | null } | null;
        }>;
        eligible_characters: Array<{
            id: number;
            name: string;
            current_health: number;
            max_health: number;
            health_percentage: number;
            is_incapacitated: boolean;
            class: { name: string; icon: string | null } | null;
        }>;
        eligible_workers: Array<{
            id: number;
            name: string;
            level: number;
            class: { name: string; icon: string | null } | null;
        }>;
    } | null;
    // Chapel-specific faction alignment and donation data
    chapelInfo: {
        level: number;
        stars: number;
        aligned_faction_id: number | null;
        aligned_faction_name: string | null;
        reputation_bonus_percent: number;
        donation_reputation_bonus: number;
        resources_per_reputation: number;
        stat_bonus_chance: number;
        can_change_faction: boolean;
        cooldown_remaining_hours: number | null;
        faction_change_penalty: number;
        large_donation_threshold: number;
        available_enclaves: Array<{
            id: number;
            name: string;
            owner_name: string | null;
        }>;
    } | null;
    // Layout data matching View.tsx for consistent navigation
    canCollect: boolean;
    pendingResources: ResourceData | null;
    nextCollectionAt: string | null;
    hoursSinceCollection: number;
    userResources: ResourceData | null;
    completedBuildingsCount: number;
    // GM tools
    isGm: boolean;
}

export default function BuildingDetail({
    hex,
    building,
    nextLevelCost,
    nextStarCost,
    nextLevelTime,
    nextStarTime,
    isOwner,
    availableCharacters,
    buildingRecipes,
    conservatoryInfo,
    mothHouseInfo,
    mysticalFocusInfo,
    toolBenchInfo,
    apothecaryInfo,
    chapelInfo,
    canCollect: initialCanCollect,
    pendingResources: initialPendingResources,
    nextCollectionAt: initialNextCollectionAt,
    userResources: initialUserResources,
    completedBuildingsCount,
    isGm,
}: Props) {
    const [selectedCharacterId, setSelectedCharacterId] = useState<number | ''>('');
    const assignForm = useForm({ character_id: '' as string | number });
    const unassignForm = useForm({ character_id: '' as string | number });

    // Upgrade progress state
    const [upgradeProgress, setUpgradeProgress] = useState(0);
    const [upgradeTimeRemaining, setUpgradeTimeRemaining] = useState('');

    // Resource collection state (matching View.tsx)
    const [isCollecting, setIsCollecting] = useState(false);
    const [canCollect, setCanCollect] = useState(initialCanCollect);
    const [pendingResources, setPendingResources] = useState(initialPendingResources);
    const [userResources, setUserResources] = useState(initialUserResources);
    const [nextCollectionAt, setNextCollectionAt] = useState(initialNextCollectionAt);
    const [minutesUntilCollection, setMinutesUntilCollection] = useState<number | null>(null);

    // GM upgrade completion state
    const [isCompletingUpgrade, setIsCompletingUpgrade] = useState(false);

    // Suppress unused variable warning - completedBuildingsCount is passed but Buildings tab is always visible now
    void completedBuildingsCount;

    // Sync userResources state with props when they change
    useEffect(() => {
        setUserResources(initialUserResources);
    }, [initialUserResources]);

    // Sync pendingResources state with props when they change
    useEffect(() => {
        setPendingResources(initialPendingResources);
    }, [initialPendingResources]);

    // Sync canCollect state with props when they change
    useEffect(() => {
        setCanCollect(initialCanCollect);
    }, [initialCanCollect]);

    // Sync nextCollectionAt state with props when they change
    useEffect(() => {
        setNextCollectionAt(initialNextCollectionAt);
    }, [initialNextCollectionAt]);

    // Helper to check if user can afford a cost
    const canAfford = useCallback((cost: ResourceData | null): boolean => {
        if (!cost || !userResources) return false;
        return (
            (cost.tea <= 0 || userResources.tea >= cost.tea) &&
            (cost.wood <= 0 || userResources.wood >= cost.wood) &&
            (cost.mystical <= 0 || userResources.mystical >= cost.mystical) &&
            (cost.medicinal <= 0 || userResources.medicinal >= cost.medicinal) &&
            (cost.edible <= 0 || userResources.edible >= cost.edible)
        );
    }, [userResources]);

    // Helper to check if user has enough of a specific resource
    const hasEnough = useCallback((resourceType: keyof ResourceData, cost: ResourceData | null): boolean => {
        if (!cost || !userResources) return true;
        const needed = cost[resourceType];
        if (needed <= 0) return true;
        return userResources[resourceType] >= needed;
    }, [userResources]);

    // Calculate how much more is needed
    const amountNeeded = useCallback((resourceType: keyof ResourceData, cost: ResourceData | null): number => {
        if (!cost || !userResources) return 0;
        const needed = cost[resourceType];
        const have = userResources[resourceType];
        return Math.max(0, needed - have);
    }, [userResources]);

    // Calculate minutes until next collection and update every minute
    useEffect(() => {
        if (!isOwner || !nextCollectionAt) return;

        const updateTimer = () => {
            const next = new Date(nextCollectionAt);
            const now = new Date();
            const diffMs = next.getTime() - now.getTime();
            const diffMins = Math.ceil(diffMs / 60000);

            if (diffMins <= 0) {
                setMinutesUntilCollection(null);
                setCanCollect(true);
                const hoursPassed = Math.floor(-diffMins / 60) + 1;
                setPendingResources({
                    tea: hex.resources.tea * hoursPassed,
                    wood: hex.resources.wood * hoursPassed,
                    mystical: hex.resources.mystical * hoursPassed,
                    medicinal: hex.resources.medicinal * hoursPassed,
                    edible: hex.resources.edible * hoursPassed,
                });
            } else {
                setMinutesUntilCollection(diffMins);
                setCanCollect(false);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 60000);
        return () => clearInterval(interval);
    }, [nextCollectionAt, isOwner, hex.resources]);

    // Handle resource collection
    const handleCollect = useCallback(() => {
        if (isCollecting || !canCollect) return;

        setIsCollecting(true);
        router.post(`/hex/${hex.id}/collect`, {}, {
            preserveScroll: true,
            onFinish: () => {
                setIsCollecting(false);
            },
        });
    }, [hex.id, isCollecting, canCollect]);

    // Format time remaining for upgrade progress
    const formatUpgradeTime = useCallback((dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();

        if (diffMs <= 0) return 'Complete!';

        const diffMins = Math.ceil(diffMs / 60000);
        if (diffMins < 60) return `~${diffMins} min`;

        const diffHours = Math.floor(diffMins / 60);
        const remainingMins = diffMins % 60;
        if (diffHours < 24) {
            return remainingMins > 0 ? `~${diffHours}h ${remainingMins}m` : `~${diffHours}h`;
        }

        const diffDays = Math.floor(diffHours / 24);
        const remainingHours = diffHours % 24;
        return remainingHours > 0 ? `~${diffDays}d ${remainingHours}h` : `~${diffDays}d`;
    }, []);

    // Calculate and update upgrade progress
    useEffect(() => {
        if (!building.isUpgrading || !building.upgradeStartedAt || !building.upgradeCompletesAt) return;

        const startTime = new Date(building.upgradeStartedAt).getTime();
        const endTime = new Date(building.upgradeCompletesAt).getTime();
        const totalDuration = endTime - startTime;

        const updateProgress = () => {
            const now = Date.now();
            const elapsed = now - startTime;
            const currentProgress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
            setUpgradeProgress(Math.round(currentProgress));
            setUpgradeTimeRemaining(formatUpgradeTime(building.upgradeCompletesAt!));

            if (currentProgress >= 100) {
                router.reload();
            }
        };

        updateProgress();
        const interval = setInterval(updateProgress, 1000);

        return () => clearInterval(interval);
    }, [building.isUpgrading, building.upgradeStartedAt, building.upgradeCompletesAt, formatUpgradeTime]);

    const isApothecary = building.buildingType.slug === 'apothecary';

    const handleAssign = () => {
        if (!selectedCharacterId) return;
        if (isApothecary) {
            router.post(`/hex/${hex.id}/buildings/${building.id}/apothecary/worker`, {
                character_id: selectedCharacterId,
            }, {
                preserveScroll: true,
                onSuccess: () => setSelectedCharacterId(''),
            });
        } else {
            assignForm.setData('character_id', selectedCharacterId);
            assignForm.patch(`/hex/${hex.id}/buildings/${building.id}/assign`, {
                preserveScroll: true,
                onSuccess: () => setSelectedCharacterId(''),
            });
        }
    };

    const handleUnassign = (characterId: number) => {
        if (isApothecary) {
            router.delete(`/hex/${hex.id}/buildings/${building.id}/apothecary/worker`, {
                preserveScroll: true,
                data: { character_id: characterId },
            });
        } else {
            unassignForm.setData('character_id', characterId);
            unassignForm.patch(`/hex/${hex.id}/buildings/${building.id}/unassign`, {
                preserveScroll: true,
            });
        }
    };

    const handleUpgrade = (type: 'level' | 'stars') => {
        router.post(`/hex/${hex.id}/buildings/${building.id}/upgrade`, { type });
    };

    const handleCancel = () => {
        router.delete(`/hex/${hex.id}/buildings/${building.id}`);
    };

    // Handle GM override to complete upgrade instantly
    const handleGmCompleteUpgrade = () => {
        if (!confirm('GM Override: Immediately complete this upgrade?')) return;
        setIsCompletingUpgrade(true);
        router.post(
            `/hex/${hex.id}/buildings/${building.id}/gm-complete`,
            {},
            {
                preserveScroll: true,
                onFinish: () => setIsCompletingUpgrade(false),
            }
        );
    };

    const slotsUsed = building.assignedCharacterCount ?? 0;
    const totalSlots = building.characterSlotCount ?? building.stars;
    const hasAvailableSlots = slotsUsed < totalSlots;

    // Sidebar content for the HexLayout - Quick Links (matching View.tsx structure)
    const sidebarContent = (
        <>
            {/* Quick Links Section - matching hex view page */}
            <HexQuickLinks
                hexId={hex.id}
                isOwner={isOwner}
                activeItem="buildings"
            />

            {/* Resource Collection Section (owner only) */}
            {isOwner && (
                <div className="p-4">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Resource Collection
                    </h2>

                    {/* Collection button when resources are available */}
                    {canCollect && (
                        <button
                            onClick={handleCollect}
                            disabled={isCollecting}
                            className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors mb-3"
                        >
                            {isCollecting ? 'Collecting...' : 'Collect Resources'}
                        </button>
                    )}

                    {/* Timer showing when next collection is available */}
                    {!canCollect && (
                        <div className="text-center">
                            <p className="text-xs text-gray-400 mb-1">Next collection in:</p>
                            <p className="text-lg font-semibold text-yellow-400">
                                {minutesUntilCollection !== null
                                    ? `${minutesUntilCollection} min${minutesUntilCollection !== 1 ? 's' : ''}`
                                    : 'Calculating...'}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* User's Total Resources (owner only) */}
            {isOwner && userResources && (
                <div className="p-4 border-t border-slate-700">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Your Resources
                    </h2>
                    <HexResources resources={userResources} compact showHeaders />
                </div>
            )}
        </>
    );

    return (
        <HexLayout
            title={`${building.buildingType.name} - ${hex.name}`}
            hex={hex}
            isOwner={isOwner}
            sidebar={sidebarContent}
        >
            {/* Main content area with paper texture */}
            <div className="p-6">
                <div className="max-w-4xl mx-auto">
                    {/* Parchment scroll container */}
                    <div className="relative">
                        {/* Paper background */}
                        <div
                            className="relative rounded-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.4), inset 0 0 60px rgba(139,90,43,0.15)',
                            }}
                        >
                            {/* Decorative corner flourishes */}
                            <CornerFlourish className="absolute top-2 left-2" />
                            <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                            <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                            <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                            {/* Aged paper edge effects */}
                            <div
                                className="absolute top-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)' }}
                            />
                            <div
                                className="absolute bottom-0 left-0 right-0 h-3"
                                style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)' }}
                            />

                            {/* Random coffee mug stain - position based on building ID */}
                            <CoffeeStain seed={building.id} />

                            {/* Inner content area */}
                            <div className="relative p-8">
                                {/* Building Header */}
                                <div className="text-center mb-6">
                                    {/* Custom decorative name (if set) */}
                                    {building.customName && (
                                        <h1
                                            className="text-4xl mb-2 text-amber-800 tracking-wider"
                                            style={{
                                                fontFamily: "'Tangerine', 'Dancing Script', cursive, serif",
                                                fontWeight: 700,
                                                textShadow: '1px 1px 2px rgba(139, 90, 43, 0.2)',
                                            }}
                                        >
                                            {building.customName}
                                        </h1>
                                    )}
                                    <h2 className={`${building.customName ? 'text-xl text-amber-700' : 'text-3xl text-amber-900'} font-serif tracking-wide mb-2`}>
                                        {building.buildingType.name}
                                    </h2>
                                    <div className="flex items-center justify-center gap-4 text-sm font-serif">
                                        <span className={`${getRankColorClass(building.buildingType.rank)}`}>
                                            Rank {building.buildingType.rank}
                                        </span>
                                        <span className="text-amber-700">•</span>
                                        <span className="text-amber-800">Level {building.level}</span>
                                        <span className="text-amber-700">•</span>
                                        <span className="text-amber-600">{'★'.repeat(building.stars)}{'☆'.repeat(5 - building.stars)}</span>
                                    </div>
                                </div>

                                <ParchmentDivider />

                                {/* Upgrade Progress - Blueprint Style */}
                                {building.isUpgrading && building.upgradeStartedAt && building.upgradeCompletesAt && (
                                    <>
                                        {/* Blueprint paper sitting on top of the parchment */}
                                        <div className="relative -mx-4 sm:-mx-6 mb-6">
                                            {/* Shadow under the blueprint */}
                                            <div
                                                className="absolute inset-x-4 bottom-0 h-4 rounded-full -mb-2"
                                                style={{
                                                    background: 'radial-gradient(ellipse, rgba(0,0,0,0.2) 0%, transparent 70%)',
                                                }}
                                            />

                                            {/* Rolled edge shadows */}
                                            <div
                                                className="absolute left-4 sm:left-6 top-2 bottom-2 w-3 rounded-l-full pointer-events-none z-20"
                                                style={{
                                                    background: 'linear-gradient(90deg, rgba(0,0,0,0.2) 0%, transparent 100%)',
                                                }}
                                            />
                                            <div
                                                className="absolute right-4 sm:right-6 top-2 bottom-2 w-3 rounded-r-full pointer-events-none z-20"
                                                style={{
                                                    background: 'linear-gradient(-90deg, rgba(0,0,0,0.2) 0%, transparent 100%)',
                                                }}
                                            />

                                            {/* Blueprint Paper */}
                                            <div
                                                className="relative mx-4 sm:mx-6 rounded-sm overflow-hidden"
                                                style={{
                                                    background: 'linear-gradient(135deg, #d4e4f0 0%, #c5d8e8 50%, #d8e8f2 100%)',
                                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25), inset 0 0 30px rgba(70,130,180,0.1)',
                                                    border: '1px solid #8bb0cc',
                                                }}
                                            >
                                                {/* Blueprint grid pattern */}
                                                <div
                                                    className="absolute inset-0 pointer-events-none"
                                                    style={{
                                                        backgroundImage: `
                                                            linear-gradient(rgba(70,130,180,0.25) 1px, transparent 1px),
                                                            linear-gradient(90deg, rgba(70,130,180,0.25) 1px, transparent 1px)
                                                        `,
                                                        backgroundSize: '20px 20px',
                                                    }}
                                                />

                                                {/* Fold creases */}
                                                <div
                                                    className="absolute left-8 top-0 bottom-0 w-px pointer-events-none"
                                                    style={{
                                                        background: 'linear-gradient(180deg, transparent 0%, rgba(70,130,180,0.15) 30%, rgba(70,130,180,0.15) 70%, transparent 100%)',
                                                    }}
                                                />
                                                <div
                                                    className="absolute right-8 top-0 bottom-0 w-px pointer-events-none"
                                                    style={{
                                                        background: 'linear-gradient(180deg, transparent 0%, rgba(70,130,180,0.15) 30%, rgba(70,130,180,0.15) 70%, transparent 100%)',
                                                    }}
                                                />

                                                {/* Content */}
                                                <div className="relative p-6">
                                                    {/* Header */}
                                                    <div className="text-center mb-5 border-b-2 border-blue-900/40 pb-3">
                                                        <div className="text-sm font-mono text-blue-900 uppercase tracking-[0.3em] font-bold mb-1">
                                                            Upgrade In Progress
                                                        </div>
                                                        <h3 className="text-xl font-mono font-bold text-blue-900 uppercase tracking-wide">
                                                            {building.isUpgradingLevel
                                                                ? `Level ${building.level} → ${building.level + 1}`
                                                                : `${'★'.repeat(building.stars)}${'☆'.repeat(5 - building.stars)} → ${'★'.repeat(building.stars + 1)}${'☆'.repeat(4 - building.stars)}`
                                                            }
                                                        </h3>
                                                    </div>

                                                    {/* Progress Section */}
                                                    <div className="flex items-start justify-between gap-8 mb-6">
                                                        {/* Left: Handwritten Progress */}
                                                        <div className="flex-1">
                                                            <div className="text-xs font-mono text-blue-900/70 uppercase tracking-wide mb-2 border-b border-blue-900/30 pb-1">
                                                                Completion Status
                                                            </div>

                                                            {/* Large handwritten percentage */}
                                                            <div className="relative">
                                                                <div
                                                                    className="text-5xl font-bold text-blue-800 mb-2"
                                                                    style={{
                                                                        fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                                                        textShadow: '1px 1px 0 rgba(70,130,180,0.2)',
                                                                    }}
                                                                >
                                                                    {upgradeProgress}%
                                                                </div>

                                                                {/* Underline flourish */}
                                                                <svg
                                                                    className="absolute -bottom-1 left-0 w-24 h-3 text-blue-800/40"
                                                                    viewBox="0 0 100 12"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                >
                                                                    <path d="M0 8 Q 30 2, 60 8 T 100 6" />
                                                                </svg>
                                                            </div>

                                                            {/* Progress bar */}
                                                            <div className="mt-4 relative">
                                                                <div className="h-3 rounded-full bg-blue-200/50 border border-blue-900/20 overflow-hidden">
                                                                    <div
                                                                        className="h-full transition-all duration-1000 ease-out rounded-full"
                                                                        style={{
                                                                            width: `${upgradeProgress}%`,
                                                                            background: 'linear-gradient(90deg, rgba(30,64,175,0.6) 0%, rgba(59,130,246,0.8) 100%)',
                                                                        }}
                                                                    />
                                                                </div>
                                                                {/* Measurement marks */}
                                                                <div className="flex justify-between mt-1 font-mono text-[10px] text-blue-900/50">
                                                                    <span>0</span>
                                                                    <span>25</span>
                                                                    <span>50</span>
                                                                    <span>75</span>
                                                                    <span>100</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Right: Typewriter Details */}
                                                        <div className="flex-1">
                                                            <div className="space-y-3 font-mono text-sm text-blue-900">
                                                                <div className="text-xs uppercase tracking-wide text-blue-900/70 border-b border-blue-900/30 pb-1">
                                                                    Project Details
                                                                </div>

                                                                <div className="flex items-end">
                                                                    <span className="uppercase tracking-wide">Est. Remaining</span>
                                                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                    <span
                                                                        className="font-bold text-blue-800"
                                                                        style={{
                                                                            fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                                                            fontSize: '1.1rem',
                                                                        }}
                                                                    >
                                                                        {upgradeTimeRemaining}
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-end">
                                                                    <span className="uppercase tracking-wide">Status</span>
                                                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                    <span
                                                                        className="font-bold text-blue-800"
                                                                        style={{
                                                                            fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                                                                            fontSize: '1.1rem',
                                                                        }}
                                                                    >
                                                                        Active
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-end">
                                                                    <span className="uppercase tracking-wide">Type</span>
                                                                    <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                    <span className="font-bold">
                                                                        {building.isUpgradingLevel ? 'Level' : 'Stars'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Cancel Stamp Button */}
                                                    {isOwner && (
                                                        <div className="flex justify-center">
                                                            <button
                                                                onClick={handleCancel}
                                                                className="relative group"
                                                            >
                                                                <div
                                                                    className="relative px-6 py-3 rounded-sm border-4 border-dashed text-center transition-all duration-200 group-hover:translate-y-0.5 group-hover:translate-x-0.5"
                                                                    style={{
                                                                        borderColor: 'rgba(185, 28, 28, 0.5)',
                                                                        background: 'linear-gradient(135deg, rgba(185, 28, 28, 0.1) 0%, rgba(185, 28, 28, 0.15) 100%)',
                                                                    }}
                                                                >
                                                                    <div className="absolute inset-1.5 rounded-sm border-2 border-red-800/30" />
                                                                    <div className="relative text-sm font-serif font-bold uppercase tracking-wider text-red-800">
                                                                        ✗ Cancel Upgrade
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* GM Override Tools for Building Upgrades */}
                                                    {isGm && (building.isUpgradingLevel || building.isUpgradingStars) && (
                                                        <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border-2 border-purple-500/50">
                                                            <h3 className="text-sm font-bold text-purple-300 uppercase mb-2 flex items-center gap-2">
                                                                <span className="text-lg">🔮</span> GM Tools
                                                            </h3>
                                                            <p className="text-xs text-purple-200/70 mb-3">
                                                                Override upgrade timer and immediately complete this building upgrade.
                                                            </p>
                                                            <button
                                                                onClick={handleGmCompleteUpgrade}
                                                                disabled={isCompletingUpgrade}
                                                                className="w-full px-4 py-2 text-sm font-bold text-white bg-purple-600 border border-purple-400 rounded-lg hover:bg-purple-500 disabled:opacity-50 transition-all cursor-pointer"
                                                            >
                                                                {isCompletingUpgrade ? 'Completing...' : 'Complete Upgrade Now'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Paper texture */}
                                                <div
                                                    className="absolute inset-0 pointer-events-none opacity-25"
                                                    style={{
                                                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <ParchmentDivider />
                                    </>
                                )}

                                {/* Building Description */}
                                {building.buildingType.description && (
                                    <div className="mb-6">
                                        <h2 className="text-xl font-serif text-amber-900 mb-3">Description</h2>
                                        <p className="text-amber-800 font-serif leading-relaxed">
                                            {building.buildingType.description}
                                        </p>
                                    </div>
                                )}

                                <ParchmentDivider />

                                {/* Character Assignment Section */}
                                <div className="mb-6">
                                    <h2 className="text-xl font-serif text-amber-900 mb-4">
                                        Assigned Workers ({slotsUsed}/{totalSlots} slots)
                                    </h2>

                                    {/* Assigned Characters List */}
                                    {building.assignedCharacters && building.assignedCharacters.length > 0 ? (
                                        <div className="space-y-3 mb-4">
                                            {building.assignedCharacters.map((character) => (
                                                <div
                                                    key={character.id}
                                                    className="flex items-center justify-between bg-amber-100/60 rounded-lg p-3 border border-amber-400/40"
                                                >
                                                    <div>
                                                        <div className="font-serif font-medium text-amber-900">{character.name}</div>
                                                        <div className="text-sm font-serif text-amber-700">
                                                            {character.class.label} • Slot {character.slot}
                                                        </div>
                                                    </div>
                                                    {isOwner && (
                                                        <button
                                                            onClick={() => handleUnassign(character.id)}
                                                            disabled={unassignForm.processing}
                                                            className="px-3 py-1 text-sm bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white rounded transition-colors font-serif"
                                                        >
                                                            {unassignForm.processing ? 'Removing...' : 'Remove'}
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-amber-700 font-serif italic mb-4">No characters assigned to this building.</p>
                                    )}

                                    {/* Assign Character Form */}
                                    {isOwner && hasAvailableSlots && availableCharacters.length > 0 && (
                                        <div className="border-t border-amber-400/30 pt-4">
                                            <h3 className="text-sm font-serif font-medium text-amber-900 mb-2">Assign a Character</h3>
                                            <div className="flex gap-2">
                                                <select
                                                    value={selectedCharacterId}
                                                    onChange={(e) => setSelectedCharacterId(e.target.value ? Number(e.target.value) : '')}
                                                    className="flex-1 bg-white/70 border border-amber-400/50 rounded px-3 py-2 text-amber-900 font-serif focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                >
                                                    <option value="">Select a character...</option>
                                                    {availableCharacters.map((char) => (
                                                        <option key={char.id} value={char.id}>
                                                            {char.name} ({char.class.label})
                                                        </option>
                                                    ))}
                                                </select>
                                                <button
                                                    onClick={handleAssign}
                                                    disabled={!selectedCharacterId || assignForm.processing}
                                                    className="px-4 py-2 bg-green-700 hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors font-serif"
                                                >
                                                    {assignForm.processing ? 'Assigning...' : 'Assign'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* No available characters message */}
                                    {isOwner && hasAvailableSlots && availableCharacters.length === 0 && (
                                        <div className="border-t border-amber-400/30 pt-4">
                                            <p className="text-amber-700 font-serif text-sm italic">
                                                No available characters to assign. All your characters are already assigned to buildings.
                                            </p>
                                        </div>
                                    )}

                                    {/* All slots filled message */}
                                    {isOwner && !hasAvailableSlots && (
                                        <div className="border-t border-amber-400/30 pt-4">
                                            <p className="text-amber-700 font-serif text-sm italic">
                                                All character slots are filled. Upgrade stars to add more slots.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Upgrade Options - Blueprint Paper Style */}
                                {isOwner && !building.isUpgrading && building.isConstructionComplete && (building.canUpgradeLevel || building.canUpgradeStars) && (
                                    <>
                                        <ParchmentDivider />
                                        {/* Blueprint paper sitting on top of the parchment */}
                                        <div className="relative -mx-4 sm:-mx-6 mb-6">
                                            {/* Shadow under the blueprint */}
                                            <div
                                                className="absolute inset-x-4 bottom-0 h-4 rounded-full -mb-2"
                                                style={{
                                                    background: 'radial-gradient(ellipse, rgba(0,0,0,0.2) 0%, transparent 70%)',
                                                }}
                                            />

                                            {/* Blueprint Paper */}
                                            <div
                                                className="relative mx-4 sm:mx-6 rounded-sm overflow-hidden"
                                                style={{
                                                    background: 'linear-gradient(135deg, #d4e4f0 0%, #c5d8e8 50%, #d8e8f2 100%)',
                                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25), inset 0 0 30px rgba(70,130,180,0.1)',
                                                    border: '1px solid #8bb0cc',
                                                }}
                                            >
                                                {/* Blueprint grid pattern */}
                                                <div
                                                    className="absolute inset-0 pointer-events-none"
                                                    style={{
                                                        backgroundImage: `
                                                            linear-gradient(rgba(70,130,180,0.25) 1px, transparent 1px),
                                                            linear-gradient(90deg, rgba(70,130,180,0.25) 1px, transparent 1px)
                                                        `,
                                                        backgroundSize: '20px 20px',
                                                    }}
                                                />

                                                {/* Content */}
                                                <div className="relative p-6">
                                                    {/* Header */}
                                                    <div className="text-center mb-5 border-b-2 border-blue-900/40 pb-3">
                                                        <div className="text-sm font-mono text-blue-900 uppercase tracking-[0.3em] font-bold">
                                                            Upgrade Specifications
                                                        </div>
                                                    </div>

                                                    <div className="grid gap-6 sm:grid-cols-2">
                                                        {/* Level Upgrade Blueprint */}
                                                        {building.canUpgradeLevel && nextLevelCost && (
                                                            <div className="space-y-4">
                                                                {/* Title */}
                                                                <h3 className="font-mono font-bold text-blue-900 text-lg uppercase tracking-wide text-center border-b border-blue-900/30 pb-2">
                                                                    Level {building.level} → {building.level + 1}
                                                                </h3>

                                                                {/* Specifications */}
                                                                <div className="space-y-2 font-mono text-sm text-blue-900">
                                                                    <div className="flex items-end">
                                                                        <span className="uppercase tracking-wide">Est. Time</span>
                                                                        <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                        <span className="font-bold">{nextLevelTime ? formatDuration(nextLevelTime) : 'N/A'}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Materials */}
                                                                <div>
                                                                    <div className="text-xs font-mono text-blue-900 uppercase tracking-wide font-bold mb-2 border-b border-blue-900/40 pb-1">
                                                                        Materials Required
                                                                    </div>
                                                                    <div className="space-y-1 font-mono text-sm text-blue-900">
                                                                        {nextLevelCost.tea > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Tea</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('tea', nextLevelCost) ? 'text-red-700' : ''}`}>{nextLevelCost.tea}</span>
                                                                                {!hasEnough('tea', nextLevelCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('tea', nextLevelCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextLevelCost.wood > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Wood</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('wood', nextLevelCost) ? 'text-red-700' : ''}`}>{nextLevelCost.wood}</span>
                                                                                {!hasEnough('wood', nextLevelCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('wood', nextLevelCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextLevelCost.mystical > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Mystical</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('mystical', nextLevelCost) ? 'text-red-700' : ''}`}>{nextLevelCost.mystical}</span>
                                                                                {!hasEnough('mystical', nextLevelCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('mystical', nextLevelCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextLevelCost.medicinal > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Medicinal</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('medicinal', nextLevelCost) ? 'text-red-700' : ''}`}>{nextLevelCost.medicinal}</span>
                                                                                {!hasEnough('medicinal', nextLevelCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('medicinal', nextLevelCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextLevelCost.edible > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Edible</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('edible', nextLevelCost) ? 'text-red-700' : ''}`}>{nextLevelCost.edible}</span>
                                                                                {!hasEnough('edible', nextLevelCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('edible', nextLevelCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Approve Stamp Button - only show if can afford */}
                                                                {canAfford(nextLevelCost) && (
                                                                    <div
                                                                        className="relative cursor-pointer group"
                                                                        onClick={() => handleUpgrade('level')}
                                                                    >
                                                                        <div
                                                                            className="relative p-3 rounded-sm border-4 border-dashed text-center transition-all duration-200 group-hover:translate-y-0.5 group-hover:translate-x-0.5"
                                                                            style={{
                                                                                borderColor: 'rgba(22, 101, 52, 0.5)',
                                                                                background: 'linear-gradient(135deg, rgba(22, 101, 52, 0.1) 0%, rgba(22, 101, 52, 0.15) 100%)',
                                                                            }}
                                                                        >
                                                                            <div className="absolute inset-1.5 rounded-sm border-2 border-green-800/30" />
                                                                            <div className="relative text-sm font-serif font-bold uppercase tracking-wider text-green-800">
                                                                                ✓ Approve Upgrade
                                                                            </div>
                                                                        </div>
                                                                        <div className="absolute inset-0 rounded-sm bg-green-900/15 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Star Upgrade Blueprint */}
                                                        {building.canUpgradeStars && nextStarCost && (
                                                            <div className="space-y-4">
                                                                {/* Title */}
                                                                <h3 className="font-mono font-bold text-blue-900 text-lg uppercase tracking-wide text-center border-b border-blue-900/30 pb-2">
                                                                    {'★'.repeat(building.stars)}{'☆'.repeat(5 - building.stars)} → {'★'.repeat(building.stars + 1)}{'☆'.repeat(4 - building.stars)}
                                                                </h3>

                                                                {/* Specifications */}
                                                                <div className="space-y-2 font-mono text-sm text-blue-900">
                                                                    <div className="flex items-end">
                                                                        <span className="uppercase tracking-wide">Est. Time</span>
                                                                        <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                        <span className="font-bold">{nextStarTime ? formatDuration(nextStarTime) : 'N/A'}</span>
                                                                    </div>
                                                                    <div className="flex items-end">
                                                                        <span className="uppercase tracking-wide">New Slot</span>
                                                                        <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                        <span className="font-bold">+1 Character</span>
                                                                    </div>
                                                                </div>

                                                                {/* Materials */}
                                                                <div>
                                                                    <div className="text-xs font-mono text-blue-900 uppercase tracking-wide font-bold mb-2 border-b border-blue-900/40 pb-1">
                                                                        Materials Required
                                                                    </div>
                                                                    <div className="space-y-1 font-mono text-sm text-blue-900">
                                                                        {nextStarCost.tea > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Tea</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('tea', nextStarCost) ? 'text-red-700' : ''}`}>{nextStarCost.tea}</span>
                                                                                {!hasEnough('tea', nextStarCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('tea', nextStarCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextStarCost.wood > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Wood</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('wood', nextStarCost) ? 'text-red-700' : ''}`}>{nextStarCost.wood}</span>
                                                                                {!hasEnough('wood', nextStarCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('wood', nextStarCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextStarCost.mystical > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Mystical</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('mystical', nextStarCost) ? 'text-red-700' : ''}`}>{nextStarCost.mystical}</span>
                                                                                {!hasEnough('mystical', nextStarCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('mystical', nextStarCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextStarCost.medicinal > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Medicinal</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('medicinal', nextStarCost) ? 'text-red-700' : ''}`}>{nextStarCost.medicinal}</span>
                                                                                {!hasEnough('medicinal', nextStarCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('medicinal', nextStarCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {nextStarCost.edible > 0 && (
                                                                            <div className="flex items-end">
                                                                                <span>Edible</span>
                                                                                <span className="flex-1 border-b border-dotted border-blue-900/40 mx-2" />
                                                                                <span className={`font-bold ${!hasEnough('edible', nextStarCost) ? 'text-red-700' : ''}`}>{nextStarCost.edible}</span>
                                                                                {!hasEnough('edible', nextStarCost) && (
                                                                                    <span className="ml-2 text-red-600 text-xs italic font-handwriting" style={{ fontFamily: 'cursive' }}>
                                                                                        need {amountNeeded('edible', nextStarCost)}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Approve Stamp Button - only show if can afford */}
                                                                {canAfford(nextStarCost) && (
                                                                    <div
                                                                        className="relative cursor-pointer group"
                                                                        onClick={() => handleUpgrade('stars')}
                                                                    >
                                                                        <div
                                                                            className="relative p-3 rounded-sm border-4 border-dashed text-center transition-all duration-200 group-hover:translate-y-0.5 group-hover:translate-x-0.5"
                                                                            style={{
                                                                                borderColor: 'rgba(22, 101, 52, 0.5)',
                                                                                background: 'linear-gradient(135deg, rgba(22, 101, 52, 0.1) 0%, rgba(22, 101, 52, 0.15) 100%)',
                                                                            }}
                                                                        >
                                                                            <div className="absolute inset-1.5 rounded-sm border-2 border-green-800/30" />
                                                                            <div className="relative text-sm font-serif font-bold uppercase tracking-wider text-green-800">
                                                                                ✓ Approve Upgrade
                                                                            </div>
                                                                        </div>
                                                                        <div className="absolute inset-0 rounded-sm bg-green-900/15 opacity-0 group-hover:opacity-100 transition-opacity -z-10 translate-y-1 translate-x-1" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Paper texture */}
                                                <div
                                                    className="absolute inset-0 pointer-events-none opacity-25"
                                                    style={{
                                                        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Max level/stars message - on parchment */}
                                {isOwner && !building.isUpgrading && building.isConstructionComplete && !building.canUpgradeLevel && !building.canUpgradeStars && (
                                    <>
                                        <ParchmentDivider />
                                        <div className="text-center mb-6">
                                            <div className="inline-block px-6 py-3 border-2 border-dashed border-green-700/50 rounded-lg bg-green-100/30">
                                                <span className="font-serif text-green-800 italic">
                                                    ✓ This building is at maximum level and stars
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Available Recipes Section */}
                                {building.isConstructionComplete && buildingRecipes.length > 0 && (
                                    <>
                                        <ParchmentDivider />
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <h2 className="text-xl font-serif text-amber-900">Available Blueprints</h2>
                                                {isOwner && (
                                                    <Link
                                                        href={`/hex/${hex.id}/crafting`}
                                                        className="text-sm font-serif text-amber-700 hover:text-amber-900 transition-colors"
                                                    >
                                                        Go to Crafting →
                                                    </Link>
                                                )}
                                            </div>
                                            <p className="text-sm font-serif text-amber-700 italic mb-4">
                                                These items can be crafted at this {building.buildingType.name}.
                                            </p>
                                            <div className="grid gap-3 sm:grid-cols-2">
                                                {buildingRecipes.map((recipe) => (
                                                    <div
                                                        key={recipe.id}
                                                        className={`bg-amber-100/50 rounded-lg p-3 border ${
                                                            recipe.is_available
                                                                ? 'border-amber-400/50'
                                                                : 'border-amber-300/30 opacity-60'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            {recipe.image.small && (
                                                                <img
                                                                    src={recipe.image.small}
                                                                    alt={recipe.name}
                                                                    className="w-12 h-12 object-cover rounded border border-amber-400/50"
                                                                />
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <h3 className="font-serif font-semibold truncate text-amber-900">
                                                                    {recipe.name}
                                                                </h3>
                                                                <p className="text-xs font-serif text-amber-700 mb-1">
                                                                    {recipe.rarity.label} • {recipe.crafting_time_display}
                                                                </p>
                                                                <div className="text-xs font-serif">
                                                                    {!recipe.is_available ? (
                                                                        <span className="text-amber-800">
                                                                            Requires: Level {recipe.required_level}, ★{recipe.required_stars}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-green-700">Recipe is available</span>
                                                                    )}
                                                                </div>
                                                                {recipe.is_available && (
                                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                                        {Object.entries(recipe.crafting_cost)
                                                                            .filter(([, amount]) => amount && amount > 0)
                                                                            .map(([type, amount]) => (
                                                                                <span
                                                                                    key={type}
                                                                                    className="text-xs font-serif text-amber-900"
                                                                                >
                                                                                    {amount} {RESOURCE_LABELS[type as keyof UserResources]}
                                                                                </span>
                                                                            ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Building-specific production bonuses - inside parchment */}
                                {building.isConstructionComplete && (
                                    <>
                                        {/* Portal-specific: Missive Attachment Info */}
                                        {building.buildingType.slug === 'portal' && (
                                            <>
                                                <ParchmentDivider />
                                                <PortalMissiveInfo level={building.level} stars={building.stars} />
                                            </>
                                        )}

                                        {/* Camp Hall-specific: Recruitment Info */}
                                        {building.buildingType.slug === 'camp-hall' && (
                                            <>
                                                <ParchmentDivider />
                                                <CampHallRecruitmentInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Lumber Mill-specific: Wood Production Info */}
                                        {building.buildingType.slug === 'lumber-mill' && (
                                            <>
                                                <ParchmentDivider />
                                                <LumberMillInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Herbalist Hut-specific: Medicinal Production Info */}
                                        {building.buildingType.slug === 'herbalist-hut' && (
                                            <>
                                                <ParchmentDivider />
                                                <HerbalistHutInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Tea House-specific: Tea Production Info */}
                                        {building.buildingType.slug === 'tea-house' && (
                                            <>
                                                <ParchmentDivider />
                                                <TeaHouseInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Shrine-specific: Mystical Production Info */}
                                        {building.buildingType.slug === 'shrine' && (
                                            <>
                                                <ParchmentDivider />
                                                <ShrineInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Smokehouse-specific: Edible Production Info */}
                                        {building.buildingType.slug === 'smokehouse' && (
                                            <>
                                                <ParchmentDivider />
                                                <SmokehouseInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Tool Bench-specific: Equipment Slots and Crafting Bonus Info */}
                                        {building.buildingType.slug === 'tool-bench' && toolBenchInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <ToolBenchEquipmentInfo
                                                    toolBenchInfo={toolBenchInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    isOwner={isOwner}
                                                />
                                            </>
                                        )}

                                        {/* Storage Shed-specific: Material Refund Info */}
                                        {building.buildingType.slug === 'storage-shed' && (
                                            <>
                                                <ParchmentDivider />
                                                <StorageShedInfo
                                                    level={building.level}
                                                    stars={building.stars}
                                                    assignedCount={building.assignedCharacterCount || 0}
                                                />
                                            </>
                                        )}

                                        {/* Conservatory-specific: Resource Production */}
                                        {building.buildingType.slug === 'conservatory' && conservatoryInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <ConservatoryProductionInfo
                                                    conservatoryInfo={conservatoryInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    isOwner={isOwner}
                                                    isGm={isGm}
                                                />
                                            </>
                                        )}

                                        {/* Moth House-specific: Character Training */}
                                        {building.buildingType.slug === 'moth-house' && mothHouseInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <MothHouseTrainingInfo
                                                    mothHouseInfo={mothHouseInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    isOwner={isOwner}
                                                    isGm={isGm}
                                                />
                                            </>
                                        )}

                                        {/* Mystical Focus-specific: Item Processing */}
                                        {building.buildingType.slug === 'mystical-focus' && mysticalFocusInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <MysticalFocusProcessingInfo
                                                    mysticalFocusInfo={mysticalFocusInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    isOwner={isOwner}
                                                    isGm={isGm}
                                                />
                                            </>
                                        )}

                                        {/* Apothecary-specific: Character Healing */}
                                        {building.buildingType.slug === 'apothecary' && apothecaryInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <ApothecaryHealingInfo
                                                    apothecaryInfo={apothecaryInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    isOwner={isOwner}
                                                    isGm={isGm}
                                                />
                                            </>
                                        )}

                                        {/* Chapel-specific: Faction Alignment & Donations */}
                                        {building.buildingType.slug === 'chapel' && chapelInfo && (
                                            <>
                                                <ParchmentDivider />
                                                <ChapelManagementInfo
                                                    chapelInfo={chapelInfo}
                                                    hexId={hex.id}
                                                    buildingId={building.id}
                                                    hexResources={hex.resources}
                                                    isOwner={isOwner}
                                                    isGm={isGm}
                                                />
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Custom Name Notes Card (owner only) */}
                                {isOwner && (
                                    <CustomNameCard
                                        hexId={hex.id}
                                        buildingId={building.id}
                                        customName={building.customName}
                                    />
                                )}

                                {/* Bottom flourish */}
                                <div className="flex items-center justify-center gap-2 mt-8">
                                    <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                                    <span className="text-amber-800/30 text-sm">❧</span>
                                    <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                                </div>
                            </div>
                        </div>

                        {/* Shadow beneath the parchment */}
                        <div
                            className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                            style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                        />
                    </div>
                </div>
            </div>
        </HexLayout>
    );
}

/**
 * Conservatory-specific component showing resource production UI.
 * Styled for parchment theme with production controls.
 */
function ConservatoryProductionInfo({
    conservatoryInfo,
    hexId,
    buildingId,
    isOwner,
    isGm,
}: {
    conservatoryInfo: NonNullable<Props['conservatoryInfo']>;
    hexId: number;
    buildingId: number;
    isOwner: boolean;
    isGm: boolean;
}) {
    const [selectedResource, setSelectedResource] = useState<string>('tea');
    const [timeRemaining, setTimeRemaining] = useState<number | null>(conservatoryInfo.time_remaining_minutes);
    const [isStarting, setIsStarting] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);

    const handleGmComplete = () => {
        if (!confirm('GM Override: Immediately complete this production and send resources via Missive?')) {
            return;
        }
        setIsCompleting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/conservatory/complete`,
            {},
            {
                preserveScroll: true,
                onFinish: () => setIsCompleting(false),
            }
        );
    };

    // Update time remaining every minute
    useEffect(() => {
        if (!conservatoryInfo.is_producing || conservatoryInfo.is_complete) {
            setTimeRemaining(null);
            return;
        }

        // Calculate initial time from completes_at
        const updateTime = () => {
            if (conservatoryInfo.completes_at) {
                const completesAt = new Date(conservatoryInfo.completes_at);
                const now = new Date();
                const remaining = Math.max(0, Math.floor((completesAt.getTime() - now.getTime()) / 60000));
                setTimeRemaining(remaining);
            }
        };

        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, [conservatoryInfo.is_producing, conservatoryInfo.is_complete, conservatoryInfo.completes_at]);

    const handleStartProduction = () => {
        setIsStarting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/conservatory/start`,
            { resource: selectedResource },
            {
                preserveScroll: true,
                onFinish: () => setIsStarting(false),
            }
        );
    };

    const handleCancelProduction = () => {
        if (!confirm('Are you sure you want to cancel production? No resources will be gained.')) {
            return;
        }
        setIsCanceling(true);
        router.delete(
            `/hex/${hexId}/buildings/${buildingId}/conservatory`,
            {
                preserveScroll: true,
                onFinish: () => setIsCanceling(false),
            }
        );
    };

    // Resource options
    const resources = ['tea', 'wood', 'mystical', 'medicinal', 'edible'] as const;

    // Format time for display
    const formatProductionTime = (minutes: number): string => {
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
        return `${minutes}m`;
    };

    // Level production amounts (flat amount of resources produced)
    const LEVEL_AMOUNTS: Record<number, number> = { 1: 25, 2: 50, 3: 75, 4: 100, 5: 125 };
    const STAR_HOURS: Record<number, number> = { 1: 24, 2: 20, 3: 16, 4: 12, 5: 8 };

    return (
        <div className="space-y-4">
            <h2 className="text-lg font-serif font-bold text-amber-900 border-b border-amber-400/50 pb-2">
                Resource Production
            </h2>

            {/* Current Status */}
            <div className="p-4 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-3">
                    Production Status
                </h3>

                {conservatoryInfo.is_producing ? (
                    <div className="space-y-3">
                        {/* Currently producing indicator */}
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${conservatoryInfo.is_complete ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                            <span className="text-amber-800 font-serif">
                                {conservatoryInfo.is_complete ? 'Production Complete!' : 'Producing...'}
                            </span>
                        </div>

                        {/* Resource being produced */}
                        <div className="flex justify-between items-center">
                            <span className="text-amber-700 font-serif">Resource:</span>
                            <span className="text-amber-900 font-medium capitalize">
                                {conservatoryInfo.current_resource}
                            </span>
                        </div>

                        {/* Expected amount */}
                        <div className="flex justify-between items-center">
                            <span className="text-amber-700 font-serif">Expected Amount:</span>
                            <span className="text-green-700 font-bold">
                                +{conservatoryInfo.production_amount || 0}
                            </span>
                        </div>

                        {/* Time remaining or complete message */}
                        {conservatoryInfo.is_complete ? (
                            <div className="text-center py-2 bg-green-100/50 rounded-lg border border-green-400/30">
                                <p className="text-green-800 font-serif text-sm">
                                    Check your Missives to collect your resources!
                                </p>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center">
                                <span className="text-amber-700 font-serif">Time Remaining:</span>
                                <span className="text-amber-900 font-medium">
                                    {timeRemaining !== null ? formatProductionTime(timeRemaining) : '...'}
                                </span>
                            </div>
                        )}

                        {/* Cancel button (only if not complete and owner) */}
                        {isOwner && !conservatoryInfo.is_complete && (
                            <button
                                onClick={handleCancelProduction}
                                disabled={isCanceling}
                                className="w-full mt-2 px-4 py-2 text-sm font-serif font-medium text-red-800 bg-red-100/50 border border-red-400/50 rounded-lg hover:bg-red-200/50 disabled:opacity-50"
                            >
                                {isCanceling ? 'Canceling...' : 'Cancel Production'}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Ready to produce */}
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-amber-800 font-serif">Ready for Production</span>
                        </div>

                        <p className="text-sm text-amber-700 font-serif">
                            Select a resource to produce. The Conservatory will generate bonus resources
                            based on your hex's natural resource values.
                        </p>

                        {/* Resource selection */}
                        {isOwner && conservatoryInfo.can_start_production && (
                            <div className="space-y-3">
                                <label className="block text-sm font-serif font-medium text-amber-900">
                                    Choose Resource
                                </label>
                                <div className="grid grid-cols-1 gap-2">
                                    {resources.map((resource) => {
                                        const isSelected = selectedResource === resource;

                                        return (
                                            <button
                                                key={resource}
                                                onClick={() => setSelectedResource(resource)}
                                                className={`flex justify-between items-center px-4 py-3 rounded-lg border transition-all ${
                                                    isSelected
                                                        ? 'bg-amber-200/60 border-amber-500'
                                                        : 'bg-amber-100/30 border-amber-400/30 hover:bg-amber-100/50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-3 h-3 rounded-full border-2 ${isSelected ? 'bg-amber-600 border-amber-600' : 'border-amber-400'}`} />
                                                    <span className="text-amber-900 font-serif capitalize">{resource}</span>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-green-700 font-bold">+{conservatoryInfo.level_production_amount}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Production info */}
                                <div className="flex justify-between items-center text-sm text-amber-700 font-serif">
                                    <span>Production Time:</span>
                                    <span className="text-amber-900 font-medium">{conservatoryInfo.production_hours} hours</span>
                                </div>

                                {/* Start button */}
                                <button
                                    onClick={handleStartProduction}
                                    disabled={isStarting}
                                    className="w-full px-4 py-3 text-sm font-serif font-bold text-amber-900 bg-gradient-to-b from-amber-300 to-amber-400 border-2 border-amber-600 rounded-lg shadow-md hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 transition-all"
                                >
                                    {isStarting ? 'Starting...' : `Start Producing ${selectedResource.charAt(0).toUpperCase() + selectedResource.slice(1)}`}
                                </button>
                            </div>
                        )}

                        {!isOwner && (
                            <p className="text-sm text-amber-600 font-serif italic">
                                Only the hex owner can start production.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* How It Works */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">How It Works</h3>
                <div className="text-xs font-serif text-amber-700 space-y-2">
                    <p>
                        The Conservatory produces resources of your chosen type.
                        Higher levels increase the amount produced, while more stars reduce production time.
                    </p>
                    <p>
                        When production completes, your resources are delivered via Missive from The Conservatory.
                        Collect them by reading the missive!
                    </p>
                </div>
            </div>

            {/* Level Production Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Production Amount by Level</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Resources Produced</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${conservatoryInfo.level === lvl ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        +{LEVEL_AMOUNTS[lvl]}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Star Production Time Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Production Time by Stars</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Production Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${conservatoryInfo.stars === s ? 'bg-blue-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{'★'.repeat(s)}{'☆'.repeat(5-s)}</td>
                                    <td className="py-2 px-3 text-center text-blue-700 font-medium">
                                        {STAR_HOURS[s]} hours
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Current Stats */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Your Conservatory</h4>
                <div className="grid grid-cols-2 gap-4 text-sm font-serif">
                    <div>
                        <span className="text-amber-700">Level {conservatoryInfo.level}:</span>
                        <span className="ml-2 text-green-700 font-medium">+{conservatoryInfo.level_production_amount} resources</span>
                    </div>
                    <div>
                        <span className="text-amber-700">{conservatoryInfo.stars} Star{conservatoryInfo.stars !== 1 ? 's' : ''}:</span>
                        <span className="ml-2 text-blue-700 font-medium">{conservatoryInfo.production_hours}h cycle</span>
                    </div>
                </div>
            </div>

            {/* GM Override Tools - Below the parchment decoration */}
            {isGm && conservatoryInfo.is_producing && !conservatoryInfo.is_complete && (
                <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border-2 border-purple-500/50">
                    <h3 className="text-sm font-bold text-purple-300 uppercase mb-2 flex items-center gap-2">
                        <span className="text-lg">🔮</span> GM Tools
                    </h3>
                    <p className="text-xs text-purple-200/70 mb-3">
                        Override production timer and immediately complete this production cycle.
                    </p>
                    <button
                        onClick={handleGmComplete}
                        disabled={isCompleting}
                        className="w-full px-4 py-2 text-sm font-bold text-white bg-purple-600 border border-purple-400 rounded-lg hover:bg-purple-500 disabled:opacity-50 transition-all"
                    >
                        {isCompleting ? 'Completing...' : 'Complete Production Now'}
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Moth House-specific component showing character training UI.
 * Styled for parchment theme with training controls.
 */
function MothHouseTrainingInfo({
    mothHouseInfo,
    hexId,
    buildingId,
    isOwner,
    isGm,
}: {
    mothHouseInfo: NonNullable<Props['mothHouseInfo']>;
    hexId: number;
    buildingId: number;
    isOwner: boolean;
    isGm: boolean;
}) {
    const [selectedCharacterId, setSelectedCharacterId] = useState<number | ''>('');
    const [timeRemaining, setTimeRemaining] = useState<number | null>(mothHouseInfo.time_remaining_minutes);
    const [isStarting, setIsStarting] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);

    const handleGmComplete = () => {
        if (!confirm('GM Override: Immediately complete this training and award XP?')) {
            return;
        }
        setIsCompleting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/moth-house/complete`,
            {},
            {
                preserveScroll: true,
                onFinish: () => setIsCompleting(false),
            }
        );
    };

    // Update time remaining every minute
    useEffect(() => {
        if (!mothHouseInfo.is_training || mothHouseInfo.is_complete) {
            setTimeRemaining(null);
            return;
        }

        // Calculate initial time from completes_at
        const updateTime = () => {
            if (mothHouseInfo.completes_at) {
                const completesAt = new Date(mothHouseInfo.completes_at);
                const now = new Date();
                const remaining = Math.max(0, Math.floor((completesAt.getTime() - now.getTime()) / 60000));
                setTimeRemaining(remaining);
            }
        };

        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, [mothHouseInfo.is_training, mothHouseInfo.is_complete, mothHouseInfo.completes_at]);

    const handleStartTraining = () => {
        if (!selectedCharacterId) return;
        setIsStarting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/moth-house/start`,
            { character_id: selectedCharacterId },
            {
                preserveScroll: true,
                onFinish: () => setIsStarting(false),
            }
        );
    };

    const handleCancelTraining = () => {
        if (!confirm('Are you sure you want to cancel training? No XP will be awarded.')) {
            return;
        }
        setIsCanceling(true);
        router.delete(
            `/hex/${hexId}/buildings/${buildingId}/moth-house`,
            {
                preserveScroll: true,
                onFinish: () => setIsCanceling(false),
            }
        );
    };

    // Format time for display
    const formatTrainingTime = (minutes: number): string => {
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
        return `${minutes}m`;
    };

    // XP amounts by level
    const LEVEL_XP: Record<number, number> = { 1: 100, 2: 200, 3: 300, 4: 400, 5: 500 };
    // Max character level by stars
    const STAR_MAX_LEVELS: Record<number, number> = { 1: 3, 2: 4, 3: 5, 4: 6, 5: 7 };

    return (
        <div className="space-y-4">
            <h2 className="text-lg font-serif font-bold text-amber-900 border-b border-amber-400/50 pb-2">
                Character Training
            </h2>

            {/* Current Status */}
            <div className="p-4 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-3">
                    Training Status
                </h3>

                {mothHouseInfo.is_training ? (
                    <div className="space-y-3">
                        {/* Currently training indicator */}
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${mothHouseInfo.is_complete ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                            <span className="text-amber-800 font-serif">
                                {mothHouseInfo.is_complete ? 'Training Complete!' : 'Training in Progress...'}
                            </span>
                        </div>

                        {/* Character being trained */}
                        {mothHouseInfo.training_character && (
                            <div className="flex justify-between items-center">
                                <span className="text-amber-700 font-serif">Character:</span>
                                <span className="text-amber-900 font-medium">
                                    {mothHouseInfo.training_character.class?.icon && (
                                        <span className="mr-1">{mothHouseInfo.training_character.class.icon}</span>
                                    )}
                                    {mothHouseInfo.training_character.name} (Lv.{mothHouseInfo.training_character.level})
                                </span>
                            </div>
                        )}

                        {/* XP to be awarded */}
                        <div className="flex justify-between items-center">
                            <span className="text-amber-700 font-serif">XP Award:</span>
                            <span className="text-green-700 font-bold">
                                +{mothHouseInfo.xp_amount} XP
                            </span>
                        </div>

                        {/* Time remaining or complete message */}
                        {mothHouseInfo.is_complete ? (
                            <div className="text-center py-2 bg-green-100/50 rounded-lg border border-green-400/30">
                                <p className="text-green-800 font-serif text-sm">
                                    Training complete! XP has been awarded. Check your Missives!
                                </p>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center">
                                <span className="text-amber-700 font-serif">Time Remaining:</span>
                                <span className="text-amber-900 font-medium">
                                    {timeRemaining !== null ? formatTrainingTime(timeRemaining) : '...'}
                                </span>
                            </div>
                        )}

                        {/* Cancel button (only if not complete and owner) */}
                        {isOwner && !mothHouseInfo.is_complete && (
                            <button
                                type="button"
                                onClick={handleCancelTraining}
                                disabled={isCanceling}
                                className="w-full mt-2 px-4 py-2 text-sm font-serif font-medium text-red-800 bg-red-100/50 border border-red-400/50 rounded-lg hover:bg-red-200/50 disabled:opacity-50"
                            >
                                {isCanceling ? 'Canceling...' : 'Cancel Training'}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Ready to train */}
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-amber-800 font-serif">Ready for Training</span>
                        </div>

                        <p className="text-sm text-amber-700 font-serif">
                            Select an unassigned character to train. After 12 hours of training,
                            they will receive XP based on the Moth House's level.
                        </p>

                        {/* Character selection */}
                        {isOwner && mothHouseInfo.can_start_training && (
                            <div className="space-y-3">
                                {mothHouseInfo.eligible_characters.length > 0 ? (
                                    <>
                                        <label className="block text-sm font-serif font-medium text-amber-900">
                                            Choose Character
                                        </label>
                                        <div className="grid grid-cols-1 gap-2">
                                            {mothHouseInfo.eligible_characters.map((character) => {
                                                const isSelected = selectedCharacterId === character.id;

                                                return (
                                                    <button
                                                        type="button"
                                                        key={character.id}
                                                        onClick={() => setSelectedCharacterId(character.id)}
                                                        className={`flex justify-between items-center px-4 py-3 rounded-lg border transition-all ${
                                                            isSelected
                                                                ? 'bg-amber-200/60 border-amber-500'
                                                                : 'bg-amber-100/30 border-amber-400/30 hover:bg-amber-100/50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className={`w-3 h-3 rounded-full border-2 ${isSelected ? 'bg-amber-600 border-amber-600' : 'border-amber-400'}`} />
                                                            <span className="text-amber-900 font-serif">
                                                                {character.class?.icon && <span className="mr-1">{character.class.icon}</span>}
                                                                {character.name}
                                                            </span>
                                                            <span className="text-amber-600 text-sm">(Lv.{character.level})</span>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-green-700 font-bold">+{mothHouseInfo.xp_amount} XP</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Training info */}
                                        <div className="flex justify-between items-center text-sm text-amber-700 font-serif">
                                            <span>Training Time:</span>
                                            <span className="text-amber-900 font-medium">12 hours</span>
                                        </div>

                                        {/* Start button */}
                                        <button
                                            type="button"
                                            onClick={handleStartTraining}
                                            disabled={isStarting || !selectedCharacterId}
                                            className="w-full px-4 py-3 text-sm font-serif font-bold text-amber-900 bg-gradient-to-b from-amber-300 to-amber-400 border-2 border-amber-600 rounded-lg shadow-md hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 transition-all"
                                        >
                                            {isStarting ? 'Starting...' : 'Start Training'}
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-center py-4 bg-amber-100/30 rounded-lg border border-amber-400/20">
                                        <p className="text-sm text-amber-700 font-serif italic">
                                            No eligible characters available. Characters must be unassigned
                                            and at level {mothHouseInfo.max_character_level} or below.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {!isOwner && (
                            <p className="text-sm text-amber-600 font-serif italic">
                                Only the hex owner can start training.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* How It Works */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">How It Works</h3>
                <div className="text-xs font-serif text-amber-700 space-y-2">
                    <p>
                        The Moth House trains unassigned characters, granting XP based on building level.
                        Higher levels grant more XP, while more stars allow higher-level characters to train.
                    </p>
                    <p>
                        Training takes 12 hours. When complete, XP is automatically applied and
                        you'll receive a Missive notification. Check your character to level up!
                    </p>
                </div>
            </div>

            {/* Level XP Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">XP by Level</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">XP Awarded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${mothHouseInfo.level === lvl ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        +{LEVEL_XP[lvl]} XP
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Star Max Level Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Max Character Level by Stars</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Max Character Level</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${mothHouseInfo.stars === s ? 'bg-blue-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{'★'.repeat(s)}{'☆'.repeat(5-s)}</td>
                                    <td className="py-2 px-3 text-center text-blue-700 font-medium">
                                        Level {STAR_MAX_LEVELS[s]}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Current Stats */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Your Moth House</h4>
                <div className="grid grid-cols-2 gap-4 text-sm font-serif">
                    <div>
                        <span className="text-amber-700">Level {mothHouseInfo.level}:</span>
                        <span className="ml-2 text-green-700 font-medium">+{mothHouseInfo.xp_amount} XP</span>
                    </div>
                    <div>
                        <span className="text-amber-700">{mothHouseInfo.stars} Star{mothHouseInfo.stars !== 1 ? 's' : ''}:</span>
                        <span className="ml-2 text-blue-700 font-medium">Max Lv.{mothHouseInfo.max_character_level}</span>
                    </div>
                </div>
            </div>

            {/* GM Override Tools - Below the parchment decoration */}
            {isGm && mothHouseInfo.is_training && !mothHouseInfo.is_complete && (
                <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border-2 border-purple-500/50">
                    <h3 className="text-sm font-bold text-purple-300 uppercase mb-2 flex items-center gap-2">
                        <span className="text-lg">🔮</span> GM Tools
                    </h3>
                    <p className="text-xs text-purple-200/70 mb-3">
                        Override training timer and immediately complete this training session.
                    </p>
                    <button
                        onClick={handleGmComplete}
                        disabled={isCompleting}
                        className="w-full px-4 py-2 text-sm font-bold text-white bg-purple-600 border border-purple-400 rounded-lg hover:bg-purple-500 disabled:opacity-50 transition-all"
                    >
                        {isCompleting ? 'Completing...' : 'Complete Training Now'}
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Mystical Focus-specific component showing item processing UI.
 * Styled for parchment theme with upgrade/reroll controls.
 */
function MysticalFocusProcessingInfo({
    mysticalFocusInfo,
    hexId,
    buildingId,
    isOwner,
    isGm,
}: {
    mysticalFocusInfo: NonNullable<Props['mysticalFocusInfo']>;
    hexId: number;
    buildingId: number;
    isOwner: boolean;
    isGm: boolean;
}) {
    const [selectedItemId, setSelectedItemId] = useState<number | ''>('');
    const [selectedAction, setSelectedAction] = useState<'upgrade' | 'reroll'>('upgrade');
    const [selectedStat, setSelectedStat] = useState<string>('');
    const [timeRemaining, setTimeRemaining] = useState<number | null>(mysticalFocusInfo.time_remaining_minutes);
    const [isStarting, setIsStarting] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);

    // Get selected item details
    const selectedItem = selectedItemId
        ? mysticalFocusInfo.eligible_items.find((item) => item.id === selectedItemId)
        : null;

    const handleGmComplete = () => {
        if (!confirm('GM Override: Immediately complete this processing and apply changes?')) {
            return;
        }
        setIsCompleting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/mystical-focus/complete`,
            {},
            {
                preserveScroll: true,
                onFinish: () => setIsCompleting(false),
            }
        );
    };

    // Update time remaining every minute
    useEffect(() => {
        if (!mysticalFocusInfo.is_processing || mysticalFocusInfo.is_complete) {
            setTimeRemaining(null);
            return;
        }

        const updateTime = () => {
            if (mysticalFocusInfo.completes_at) {
                const completesAt = new Date(mysticalFocusInfo.completes_at);
                const now = new Date();
                const remaining = Math.max(0, Math.floor((completesAt.getTime() - now.getTime()) / 60000));
                setTimeRemaining(remaining);
            }
        };

        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, [mysticalFocusInfo.is_processing, mysticalFocusInfo.is_complete, mysticalFocusInfo.completes_at]);

    const handleStartUpgrade = () => {
        if (!selectedItemId || !selectedStat) return;
        setIsStarting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/mystical-focus/upgrade`,
            { item_id: selectedItemId, stat: selectedStat },
            {
                preserveScroll: true,
                onFinish: () => setIsStarting(false),
            }
        );
    };

    const handleStartReroll = () => {
        if (!selectedItemId) return;
        setIsStarting(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/mystical-focus/reroll`,
            { item_id: selectedItemId },
            {
                preserveScroll: true,
                onFinish: () => setIsStarting(false),
            }
        );
    };

    const handleCancelProcessing = () => {
        if (!confirm('Are you sure you want to cancel processing? The item will not be modified.')) {
            return;
        }
        setIsCanceling(true);
        router.delete(
            `/hex/${hexId}/buildings/${buildingId}/mystical-focus`,
            {
                preserveScroll: true,
                onFinish: () => setIsCanceling(false),
            }
        );
    };

    // Format time for display
    const formatProcessingTime = (minutes: number): string => {
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        }
        return `${minutes}m`;
    };

    // Level base hours
    const LEVEL_BASE_HOURS: Record<number, number> = { 1: 24, 2: 22, 3: 20, 4: 18, 5: 16 };

    return (
        <div className="space-y-4">
            <h2 className="text-lg font-serif font-bold text-amber-900 border-b border-amber-400/50 pb-2">
                Item Enhancement
            </h2>

            {/* Current Status */}
            <div className="p-4 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-3">
                    Processing Status
                </h3>

                {mysticalFocusInfo.is_processing ? (
                    <div className="space-y-3">
                        {/* Currently processing indicator */}
                        <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${mysticalFocusInfo.is_complete ? 'bg-green-500' : 'bg-purple-500 animate-pulse'}`} />
                            <span className="text-amber-800 font-serif">
                                {mysticalFocusInfo.is_complete ? 'Processing Complete!' : 'Processing in Progress...'}
                            </span>
                        </div>

                        {/* Item being processed */}
                        {mysticalFocusInfo.processing_item && (
                            <div className="flex justify-between items-center">
                                <span className="text-amber-700 font-serif">Item:</span>
                                <span className="text-amber-900 font-medium">
                                    {mysticalFocusInfo.processing_item.name}
                                </span>
                            </div>
                        )}

                        {/* Action type */}
                        <div className="flex justify-between items-center">
                            <span className="text-amber-700 font-serif">Action:</span>
                            <span className="text-purple-700 font-bold">
                                {mysticalFocusInfo.action === 'upgrade' ? 'Upgrade' : 'Reroll'}
                                {mysticalFocusInfo.action === 'upgrade' && mysticalFocusInfo.upgrade_stat && (
                                    <span className="ml-1 text-amber-600">
                                        ({mysticalFocusInfo.stat_labels[mysticalFocusInfo.upgrade_stat] || mysticalFocusInfo.upgrade_stat})
                                    </span>
                                )}
                            </span>
                        </div>

                        {/* Effect power */}
                        <div className="flex justify-between items-center">
                            <span className="text-amber-700 font-serif">Bonus:</span>
                            <span className="text-green-700 font-bold">
                                +{mysticalFocusInfo.effect_power} to {mysticalFocusInfo.action === 'upgrade' ? 'selected stat' : 'all stats'}
                            </span>
                        </div>

                        {/* Time remaining or complete message */}
                        {mysticalFocusInfo.is_complete ? (
                            <div className="text-center py-2 bg-green-100/50 rounded-lg border border-green-400/30">
                                <p className="text-green-800 font-serif text-sm">
                                    Processing complete! Your item has been enhanced. Check your Missives!
                                </p>
                            </div>
                        ) : (
                            <div className="flex justify-between items-center">
                                <span className="text-amber-700 font-serif">Time Remaining:</span>
                                <span className="text-amber-900 font-medium">
                                    {timeRemaining !== null ? formatProcessingTime(timeRemaining) : '...'}
                                </span>
                            </div>
                        )}

                        {/* Cancel button (only if not complete and owner) */}
                        {isOwner && !mysticalFocusInfo.is_complete && (
                            <button
                                type="button"
                                onClick={handleCancelProcessing}
                                disabled={isCanceling}
                                className="w-full mt-2 px-4 py-2 text-sm font-serif font-medium text-red-800 bg-red-100/50 border border-red-400/50 rounded-lg hover:bg-red-200/50 disabled:opacity-50"
                            >
                                {isCanceling ? 'Canceling...' : 'Cancel Processing'}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Ready to process */}
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500" />
                            <span className="text-amber-800 font-serif">Ready for Enhancement</span>
                        </div>

                        <p className="text-sm text-amber-700 font-serif">
                            Select an item to enhance. You can upgrade a single stat or reroll all stats.
                            The effect power is based on the building's stars (+{mysticalFocusInfo.effect_power}).
                        </p>

                        {/* Item and action selection */}
                        {isOwner && mysticalFocusInfo.can_start_action && (
                            <div className="space-y-4">
                                {mysticalFocusInfo.eligible_items.length > 0 ? (
                                    <>
                                        {/* Item selection */}
                                        <div>
                                            <label className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                                Choose Item
                                            </label>
                                            <select
                                                value={selectedItemId}
                                                onChange={(e) => {
                                                    setSelectedItemId(e.target.value ? Number(e.target.value) : '');
                                                    setSelectedStat('');
                                                }}
                                                className="w-full px-4 py-2 bg-amber-50 border border-amber-400/50 rounded-lg text-amber-900 font-serif focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                            >
                                                <option value="">Select an item...</option>
                                                {mysticalFocusInfo.eligible_items.map((item) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.name} ({item.quality})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Show item stats if selected */}
                                        {selectedItem && (
                                            <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-300/50">
                                                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Item Stats</h4>
                                                <div className="grid grid-cols-2 gap-2 text-sm">
                                                    {Object.entries(selectedItem.stats || {}).map(([stat, statData]) => (
                                                        <div key={stat} className="flex justify-between">
                                                            <span className="text-amber-700">{statData.label}:</span>
                                                            <span className="text-amber-900 font-medium">{statData.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action selection */}
                                        {selectedItem && (
                                            <div>
                                                <label className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                                    Choose Action
                                                </label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedAction('upgrade')}
                                                        className={`px-4 py-3 rounded-lg border font-serif text-sm transition-all ${
                                                            selectedAction === 'upgrade'
                                                                ? 'bg-purple-200/60 border-purple-500 text-purple-900'
                                                                : 'bg-amber-100/30 border-amber-400/30 hover:bg-amber-100/50 text-amber-800'
                                                        }`}
                                                    >
                                                        <div className="font-bold">Upgrade</div>
                                                        <div className="text-xs opacity-75">+{mysticalFocusInfo.effect_power} to one stat</div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedAction('reroll');
                                                            setSelectedStat('');
                                                        }}
                                                        className={`px-4 py-3 rounded-lg border font-serif text-sm transition-all ${
                                                            selectedAction === 'reroll'
                                                                ? 'bg-purple-200/60 border-purple-500 text-purple-900'
                                                                : 'bg-amber-100/30 border-amber-400/30 hover:bg-amber-100/50 text-amber-800'
                                                        }`}
                                                    >
                                                        <div className="font-bold">Reroll</div>
                                                        <div className="text-xs opacity-75">New stats +{mysticalFocusInfo.effect_power}</div>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Stat selection for upgrade */}
                                        {selectedItem && selectedAction === 'upgrade' && (
                                            <div>
                                                <label className="block text-sm font-serif font-medium text-amber-900 mb-2">
                                                    Choose Stat to Upgrade
                                                </label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {Object.entries(selectedItem.stats || {}).map(([stat, statData]) => (
                                                        <button
                                                            type="button"
                                                            key={stat}
                                                            onClick={() => setSelectedStat(stat)}
                                                            className={`flex justify-between items-center px-3 py-2 rounded-lg border font-serif text-sm transition-all ${
                                                                selectedStat === stat
                                                                    ? 'bg-green-200/60 border-green-500'
                                                                    : 'bg-amber-100/30 border-amber-400/30 hover:bg-amber-100/50'
                                                            }`}
                                                        >
                                                            <span className="text-amber-800">
                                                                {statData.label}
                                                            </span>
                                                            <span className="text-green-700 font-bold">
                                                                {statData.value} → {statData.value + mysticalFocusInfo.effect_power}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Processing time info */}
                                        <div className="flex justify-between items-center text-sm text-amber-700 font-serif">
                                            <span>Processing Time:</span>
                                            <span className="text-amber-900 font-medium">
                                                {mysticalFocusInfo.processing_hours} hours
                                                {mysticalFocusInfo.character_reduction > 0 && (
                                                    <span className="text-green-600 ml-1">
                                                        (-{mysticalFocusInfo.character_reduction}h from characters)
                                                    </span>
                                                )}
                                            </span>
                                        </div>

                                        {/* Start button */}
                                        <button
                                            type="button"
                                            onClick={selectedAction === 'upgrade' ? handleStartUpgrade : handleStartReroll}
                                            disabled={isStarting || !selectedItemId || (selectedAction === 'upgrade' && !selectedStat)}
                                            className="w-full px-4 py-3 text-sm font-serif font-bold text-purple-900 bg-gradient-to-b from-purple-300 to-purple-400 border-2 border-purple-600 rounded-lg shadow-md hover:from-purple-400 hover:to-purple-500 disabled:opacity-50 transition-all"
                                        >
                                            {isStarting ? 'Starting...' : selectedAction === 'upgrade' ? 'Start Upgrade' : 'Start Reroll'}
                                        </button>
                                    </>
                                ) : (
                                    <div className="text-center py-4 bg-amber-100/30 rounded-lg border border-amber-400/20">
                                        <p className="text-sm text-amber-700 font-serif italic">
                                            No eligible items available. Items must be unequipped and not be blueprints.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {!isOwner && (
                            <p className="text-sm text-amber-600 font-serif italic">
                                Only the hex owner can process items.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* How It Works */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">How It Works</h3>
                <div className="text-xs font-serif text-amber-700 space-y-2">
                    <p>
                        The Mystical Focus enhances items through mystical energies. Choose between
                        upgrading a single stat or rerolling all stats with a bonus.
                    </p>
                    <p>
                        <strong>Upgrade:</strong> Increases one stat by +{mysticalFocusInfo.effect_power} (based on stars).
                        <br />
                        <strong>Reroll:</strong> Regenerates all stats and adds +{mysticalFocusInfo.effect_power} to each.
                    </p>
                    <p>
                        Assign characters to reduce processing time by 1 hour each.
                    </p>
                </div>
            </div>

            {/* Level Time Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Base Time by Level</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Base Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${mysticalFocusInfo.level === lvl ? 'bg-purple-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-purple-700 font-medium">
                                        {LEVEL_BASE_HOURS[lvl]}h
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Stars Effect Power */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Effect Power by Stars</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Bonus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${mysticalFocusInfo.stars === s ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{'★'.repeat(s)}{'☆'.repeat(5-s)}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        +{s}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Assigned Characters */}
            {mysticalFocusInfo.assigned_characters.length > 0 && (
                <div className="mb-4">
                    <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Assigned Characters</h3>
                    <div className="p-3 bg-amber-50/50 rounded-lg border border-amber-300/50">
                        <div className="space-y-1">
                            {mysticalFocusInfo.assigned_characters.map((char) => (
                                <div key={char.id} className="flex justify-between text-sm">
                                    <span className="text-amber-800">
                                        {char.class?.icon && <span className="mr-1">{char.class.icon}</span>}
                                        {char.name}
                                    </span>
                                    <span className="text-green-600">-1h</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-amber-300/50 text-sm">
                            <span className="text-amber-700">Total Reduction:</span>
                            <span className="ml-2 text-green-700 font-medium">-{mysticalFocusInfo.character_reduction}h</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Current Stats */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Your Mystical Focus</h4>
                <div className="grid grid-cols-2 gap-4 text-sm font-serif">
                    <div>
                        <span className="text-amber-700">Level {mysticalFocusInfo.level}:</span>
                        <span className="ml-2 text-purple-700 font-medium">{mysticalFocusInfo.base_hours}h base</span>
                    </div>
                    <div>
                        <span className="text-amber-700">{mysticalFocusInfo.stars} Star{mysticalFocusInfo.stars !== 1 ? 's' : ''}:</span>
                        <span className="ml-2 text-green-700 font-medium">+{mysticalFocusInfo.effect_power} power</span>
                    </div>
                </div>
            </div>

            {/* GM Override Tools */}
            {isGm && mysticalFocusInfo.is_processing && !mysticalFocusInfo.is_complete && (
                <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border-2 border-purple-500/50">
                    <h3 className="text-sm font-bold text-purple-300 uppercase mb-2 flex items-center gap-2">
                        <span className="text-lg">🔮</span> GM Tools
                    </h3>
                    <p className="text-xs text-purple-200/70 mb-3">
                        Override processing timer and immediately complete this enhancement.
                    </p>
                    <button
                        onClick={handleGmComplete}
                        disabled={isCompleting}
                        className="w-full px-4 py-2 text-sm font-bold text-white bg-purple-600 border border-purple-400 rounded-lg hover:bg-purple-500 disabled:opacity-50 transition-all"
                    >
                        {isCompleting ? 'Completing...' : 'Complete Processing Now'}
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * Tool Bench-specific component showing equipment slot management and crafting bonus mechanics.
 * Allows owners to add/remove characters from the equipment slot bonus.
 * Styled for parchment theme.
 */
function ToolBenchEquipmentInfo({
    toolBenchInfo,
    hexId,
    buildingId,
    isOwner,
}: {
    toolBenchInfo: NonNullable<Props['toolBenchInfo']>;
    hexId: number;
    buildingId: number;
    isOwner: boolean;
}) {
    const [selectedCharacterId, setSelectedCharacterId] = useState<number | ''>('');
    const [isAdding, setIsAdding] = useState(false);
    const [isRemoving, setIsRemoving] = useState<number | null>(null);

    // Constants matching the backend ToolBenchService
    const STAR_EQUIPMENT_SLOT_LIMITS: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 2, 5: 3 };
    const LEVEL_CRAFTING_BONUS_CHANCE: Record<number, number> = { 1: 5, 2: 10, 3: 15, 4: 20, 5: 25 };
    const BASE_CRAFTING_STAT_BONUS = 5;

    const { level, stars, assigned_count: assignedCount } = toolBenchInfo;
    const hasWorkers = assignedCount > 0;
    const hasEquipmentSlotCharacters = toolBenchInfo.equipment_slot_characters.length > 0;
    const canAddMore = toolBenchInfo.available_equipment_slots > 0;

    const handleAddCharacter = () => {
        if (!selectedCharacterId) return;
        setIsAdding(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/tool-bench/equipment-slot`,
            { character_id: selectedCharacterId },
            {
                preserveScroll: true,
                onFinish: () => {
                    setIsAdding(false);
                    setSelectedCharacterId('');
                },
            }
        );
    };

    const handleRemoveCharacter = (characterId: number) => {
        setIsRemoving(characterId);
        router.delete(
            `/hex/${hexId}/buildings/${buildingId}/tool-bench/equipment-slot`,
            {
                data: { character_id: characterId },
                preserveScroll: true,
                onFinish: () => setIsRemoving(null),
            }
        );
    };

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">🔧</span>
                <h2 className="text-xl font-serif text-amber-900">Tool Bench Bonuses</h2>
            </div>

            <p className="text-amber-800 font-serif text-sm mb-4 leading-relaxed">
                The Tool Bench provides two distinct benefits: bonus equipment slots for selected characters
                and a chance to enhance stat rolls when crafting items. Workers assigned to the building
                increase the crafting bonus, while equipment slot beneficiaries are managed separately below.
            </p>

            {/* Equipment Slot Characters Section */}
            <div className="mb-6">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <span>🎒</span> Equipment Slot Bonus
                </h3>
                <p className="text-xs font-serif text-amber-700 mb-3">
                    Select up to {toolBenchInfo.equipment_slot_limit} character{toolBenchInfo.equipment_slot_limit !== 1 ? 's' : ''} to receive +1 equipment slot each.
                    {toolBenchInfo.equipment_slot_characters.length > 0 && ' Removing a character will unequip their last item if over capacity.'}
                </p>

                {/* Current Equipment Slot Characters */}
                {hasEquipmentSlotCharacters && (
                    <div className="bg-green-100/40 rounded-lg p-3 border border-green-600/40 mb-3">
                        <h4 className="text-xs font-semibold text-green-800 uppercase mb-2">
                            Characters with +1 Equipment Slot ({toolBenchInfo.equipment_slot_characters.length}/{toolBenchInfo.equipment_slot_limit})
                        </h4>
                        <div className="space-y-2">
                            {toolBenchInfo.equipment_slot_characters.map((char) => (
                                <div key={char.id} className="flex items-center justify-between bg-white/50 rounded px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        {char.class?.icon && <span>{char.class.icon}</span>}
                                        <span className="font-serif text-amber-900">{char.name}</span>
                                        <span className="text-xs text-amber-600">Lv{char.level}</span>
                                    </div>
                                    {isOwner && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCharacter(char.id)}
                                            disabled={isRemoving === char.id}
                                            className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                                        >
                                            {isRemoving === char.id ? 'Removing...' : 'Remove'}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add Character Form */}
                {isOwner && canAddMore && toolBenchInfo.eligible_characters.length > 0 && (
                    <div className="bg-amber-100/40 rounded-lg p-3 border border-amber-400/30">
                        <h4 className="text-xs font-semibold text-amber-800 uppercase mb-2">
                            Add Character ({toolBenchInfo.available_equipment_slots} slot{toolBenchInfo.available_equipment_slots !== 1 ? 's' : ''} available)
                        </h4>
                        <div className="flex gap-2">
                            <select
                                value={selectedCharacterId}
                                onChange={(e) => setSelectedCharacterId(e.target.value ? Number(e.target.value) : '')}
                                className="flex-1 px-3 py-2 text-sm font-serif bg-white/70 border border-amber-400/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                            >
                                <option value="">Select a character...</option>
                                {toolBenchInfo.eligible_characters.map((char) => (
                                    <option key={char.id} value={char.id}>
                                        {char.class?.icon || ''} {char.name} (Lv{char.level})
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={handleAddCharacter}
                                disabled={!selectedCharacterId || isAdding}
                                className="px-4 py-2 text-sm font-serif font-semibold text-white bg-green-600 border border-green-500 rounded-lg hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isAdding ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>
                )}

                {isOwner && canAddMore && toolBenchInfo.eligible_characters.length === 0 && (
                    <div className="bg-amber-100/40 rounded-lg p-3 border border-amber-400/30">
                        <p className="text-sm text-amber-700 font-serif">
                            No eligible characters available. All your characters are already receiving this bonus.
                        </p>
                    </div>
                )}

                {!canAddMore && !hasEquipmentSlotCharacters && (
                    <div className="bg-amber-200/40 rounded-lg p-3 border border-amber-500/40">
                        <p className="text-sm text-amber-800 font-serif">
                            No characters are receiving the equipment slot bonus yet.
                        </p>
                    </div>
                )}
            </div>

            {/* Current Bonuses Status */}
            <div className={`rounded-lg p-4 mb-4 border ${hasWorkers ? 'bg-green-100/40 border-green-600/40' : 'bg-amber-200/40 border-amber-500/40'}`}>
                <h3 className={`text-sm font-serif font-semibold uppercase tracking-wider mb-3 ${hasWorkers ? 'text-green-800' : 'text-amber-800'}`}>
                    Crafting Bonuses {hasWorkers ? '(Active)' : '(Inactive - No Workers)'}
                </h3>
                <div className="space-y-2 font-serif">
                    <div className="flex justify-between text-sm">
                        <span className="text-amber-700">Crafting Bonus Chance (Lv{level})</span>
                        <span className="text-green-700 font-medium">{toolBenchInfo.crafting_bonus_chance}%</span>
                    </div>
                    {hasWorkers ? (
                        <div className="flex justify-between text-sm">
                            <span className="text-amber-700">Crafting Stat Bonus ({assignedCount} workers)</span>
                            <span className="text-green-700 font-medium">+{toolBenchInfo.crafting_stat_bonus} to all stats</span>
                        </div>
                    ) : (
                        <div className="text-xs text-amber-600 mt-2">
                            Base crafting bonus is +{BASE_CRAFTING_STAT_BONUS}. Assign workers to the Tool Bench to increase the bonus by +1 each.
                        </div>
                    )}
                </div>
            </div>

            {/* Equipment Slot Limits Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Equipment Slot Limits by Stars</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Max Characters</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${stars === s ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{'★'.repeat(s)}{'☆'.repeat(5-s)}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        {STAR_EQUIPMENT_SLOT_LIMITS[s]} character{STAR_EQUIPMENT_SLOT_LIMITS[s] !== 1 ? 's' : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Crafting Bonus Table */}
            <div className="mb-4">
                <h3 className="text-sm font-serif font-semibold text-amber-900 mb-2">Crafting Bonus Chance by Level</h3>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    When crafting items, there's a chance to add bonus stats. The bonus amount is +{BASE_CRAFTING_STAT_BONUS} plus +1 for each worker.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-serif">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-800 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-800 font-medium">Chance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${level === lvl ? 'bg-green-200/30' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        {LEVEL_CRAFTING_BONUS_CHANCE[lvl]}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/**
 * Custom Name Card component for adding a decorative/roleplay name to a building.
 * Styled as a simple slip of paper with handwritten-style text.
 */
function CustomNameCard({
    hexId,
    buildingId,
    customName,
}: {
    hexId: number;
    buildingId: number;
    customName: string | null;
}) {
    const [name, setName] = useState(customName || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const hasChanged = name !== (customName || '');

    const handleSave = () => {
        setIsSaving(true);
        router.patch(
            `/hex/${hexId}/buildings/${buildingId}/custom-name`,
            { custom_name: name || null },
            {
                preserveScroll: true,
                onSuccess: () => {
                    setIsEditing(false);
                },
                onFinish: () => {
                    setIsSaving(false);
                },
            }
        );
    };

    const handleCancel = () => {
        setName(customName || '');
        setIsEditing(false);
    };

    return (
        <div className="mt-8">
            {/* Paper slip container */}
            <div
                className="relative mx-auto max-w-md"
                style={{
                    transform: 'rotate(-5deg)',
                }}
            >
                {/* Paper background */}
                <div
                    className="relative bg-white rounded-sm px-6 py-5"
                    style={{
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08), 2px 3px 6px rgba(0,0,0,0.06)',
                        background: 'linear-gradient(180deg, #fefefe 0%, #f9f7f4 100%)',
                    }}
                >
                    {/* Subtle paper texture */}
                    <div
                        className="absolute inset-0 opacity-20 pointer-events-none rounded-sm"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                        }}
                    />

                    {/* Decorative header */}
                    <div className="text-center mb-3">
                        <span className="text-amber-400/60 text-sm tracking-widest">✦ ✦ ✦</span>
                    </div>

                    {/* Label */}
                    <label className="block text-center text-xs text-amber-600/70 uppercase tracking-wider mb-3 font-serif">
                        Personal Inscription
                    </label>

                    {/* Input or Display */}
                    {isEditing ? (
                        <div className="space-y-4">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Name your establishment..."
                                maxLength={100}
                                className="w-full text-center text-lg bg-transparent border-b-2 border-amber-300/50 focus:border-amber-500/70 outline-none py-2 placeholder-amber-400/40 text-amber-900"
                                style={{
                                    fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                }}
                                autoFocus
                            />

                            {/* Action buttons */}
                            <div className="flex justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-1.5 text-xs font-serif text-amber-600 hover:text-amber-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isSaving || !hasChanged}
                                    className="px-4 py-1.5 text-xs font-serif text-white bg-amber-600 hover:bg-amber-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSaving ? 'Inscribing...' : 'Inscribe'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center">
                            {customName ? (
                                <p
                                    className="text-xl text-amber-900 mb-3"
                                    style={{
                                        fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                    }}
                                >
                                    "{customName}"
                                </p>
                            ) : (
                                <p
                                    className="text-lg text-amber-400/60 italic mb-3"
                                    style={{
                                        fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                    }}
                                >
                                    No inscription yet...
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="text-xs font-serif text-amber-600 hover:text-amber-800 underline decoration-dotted underline-offset-2 transition-colors"
                            >
                                {customName ? 'Change inscription' : 'Add inscription'}
                            </button>
                        </div>
                    )}

                    {/* Decorative footer */}
                    <div className="text-center mt-4">
                        <span className="text-amber-400/40 text-xs">~ ❧ ~</span>
                    </div>
                </div>

                {/* Paper fold effect */}
                <div
                    className="absolute -bottom-1 left-0 right-0 h-2 bg-gradient-to-t from-black/5 to-transparent rounded-b-sm"
                />
            </div>
        </div>
    );
}

/**
 * Apothecary Healing Info component
 * Shows healing status, workers, characters being healed, and available characters.
 */
function ApothecaryHealingInfo({
    apothecaryInfo,
    hexId,
    buildingId,
    isOwner,
    isGm,
}: {
    apothecaryInfo: NonNullable<Props['apothecaryInfo']>;
    hexId: number;
    buildingId: number;
    isOwner: boolean;
    isGm: boolean;
}) {
    const [healingStates, setHealingStates] = useState<Map<number, number | null>>(
        new Map(apothecaryInfo.healing_characters.map(c => [c.id, c.time_remaining_minutes]))
    );

    // Update countdown timer
    useEffect(() => {
        const activeHealings = apothecaryInfo.healing_characters.filter(c => !c.is_complete && c.completes_at);
        if (activeHealings.length === 0) return;

        const interval = setInterval(() => {
            const now = Date.now();
            setHealingStates(prev => {
                const next = new Map(prev);
                for (const character of activeHealings) {
                    if (character.completes_at) {
                        const completesAt = new Date(character.completes_at);
                        const remaining = Math.max(0, Math.floor((completesAt.getTime() - now) / 60000));
                        next.set(character.id, remaining);
                    }
                }
                return next;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [apothecaryInfo.healing_characters]);

    const handleStartHealing = (characterId: number) => {
        router.post(`/hex/${hexId}/buildings/${buildingId}/apothecary/start`, {
            character_id: characterId,
        });
    };

    const handleCancelHealing = (characterId: number) => {
        router.delete(`/hex/${hexId}/buildings/${buildingId}/apothecary`, {
            data: { character_id: characterId },
        });
    };

    const handleCompleteHealing = (characterId: number) => {
        router.post(`/hex/${hexId}/buildings/${buildingId}/apothecary/complete`, {
            character_id: characterId,
        });
    };

    const getHealthBarColor = (percentage: number, isIncapacitated: boolean) => {
        if (isIncapacitated) return 'bg-gray-400';
        if (percentage > 70) return 'bg-green-500';
        if (percentage > 30) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <span className="text-lg">⚗️</span>
                <h3 className="font-['Cinzel'] text-lg text-amber-900/90">Apothecary Healing</h3>
            </div>

            {/* Healing Info Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-200/30">
                    <div className="text-amber-700/60 text-xs uppercase tracking-wide mb-1">Healing Time</div>
                    <div className="text-amber-900 font-medium">
                        {apothecaryInfo.healing_duration_minutes} min
                        {apothecaryInfo.time_reduction_percent > 0 && (
                            <span className="text-green-600 ml-1">(-{apothecaryInfo.time_reduction_percent}%)</span>
                        )}
                    </div>
                    <div className="text-xs text-amber-600/70 mt-1">
                        Base: {apothecaryInfo.base_healing_duration_minutes} min
                    </div>
                </div>
                <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-200/30">
                    <div className="text-amber-700/60 text-xs uppercase tracking-wide mb-1">Passive Bonus</div>
                    <div className="text-amber-900 font-medium">+{apothecaryInfo.passive_body_bonus} Body</div>
                    <div className="text-xs text-amber-600/70 mt-1">
                        Level: +{apothecaryInfo.passive_body_bonus_from_level}
                        {apothecaryInfo.passive_body_bonus_from_workers > 0 && (
                            <>, Workers: +{apothecaryInfo.passive_body_bonus_from_workers}</>
                        )}
                    </div>
                </div>
            </div>

            {/* Capacity */}
            <div className="text-sm text-amber-700/70">
                Workers: {apothecaryInfo.worker_count}/{apothecaryInfo.slot_count} &bull; Patients: {apothecaryInfo.patient_count}/{apothecaryInfo.patient_slot_count}
            </div>

            {/* Worker Bonuses Info */}
            {apothecaryInfo.worker_count > 0 && (
                <div className="text-sm text-amber-700/70">
                    Workers provide: -10% healing time each, +5 passive Body each
                </div>
            )}

            {/* Characters Currently Healing */}
            {apothecaryInfo.healing_characters.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-amber-800/80">Characters Healing</h4>
                    <div className="space-y-2">
                        {apothecaryInfo.healing_characters.map((character) => {
                            const timeRemaining = healingStates.get(character.id) ?? character.time_remaining_minutes;
                            return (
                                <div
                                    key={character.id}
                                    className="bg-white/60 rounded-lg p-3 border border-amber-200/30 shadow-sm"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${character.is_complete ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                                            <span className="font-medium text-amber-900">{character.name}</span>
                                            {character.class && (
                                                <span className="text-xs text-amber-600/70">({character.class.name})</span>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            {character.is_complete ? (
                                                <span className="text-green-600 font-medium text-sm">Ready!</span>
                                            ) : (
                                                <span className="text-amber-600 text-sm">
                                                    {timeRemaining !== null ? formatDuration(timeRemaining) : '...'}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Health bar (shows current damage being healed) */}
                                    <div className="mt-2">
                                        <div className="flex justify-between text-xs text-amber-600/70 mb-1">
                                            <span>HP: {character.current_health}/{character.max_health}</span>
                                            <span>{Math.round(character.health_percentage)}%</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${getHealthBarColor(character.health_percentage, character.is_incapacitated)} transition-all`}
                                                style={{ width: `${character.health_percentage}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="mt-2 flex gap-2">
                                        {character.is_complete && isGm && (
                                            <button
                                                onClick={() => handleCompleteHealing(character.id)}
                                                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 cursor-pointer"
                                            >
                                                GM: Force Complete
                                            </button>
                                        )}
                                        {isOwner && !character.is_complete && (
                                            <button
                                                onClick={() => handleCancelHealing(character.id)}
                                                className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 cursor-pointer"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                        {isGm && !character.is_complete && (
                                            <button
                                                onClick={() => handleCompleteHealing(character.id)}
                                                className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 cursor-pointer"
                                            >
                                                GM: Complete Now
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Start New Healing */}
            {isOwner && apothecaryInfo.can_accept_patients && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-amber-800/80">Add Character for Healing</h4>
                    {apothecaryInfo.eligible_characters.length > 0 ? (
                        <div className="space-y-2">
                            {apothecaryInfo.eligible_characters.map((character) => (
                                <div
                                    key={character.id}
                                    className="bg-white/40 rounded-lg p-3 border border-amber-200/30 flex items-center justify-between"
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-amber-900">{character.name}</span>
                                            {character.class && (
                                                <span className="text-xs text-amber-600/70">({character.class.name})</span>
                                            )}
                                            {character.is_incapacitated && (
                                                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">KO</span>
                                            )}
                                        </div>
                                        <div className="mt-1">
                                            <div className="flex justify-between text-xs text-amber-600/70 mb-0.5">
                                                <span>HP: {character.current_health}/{character.max_health}</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden w-24">
                                                <div
                                                    className={`h-full ${getHealthBarColor(character.health_percentage, character.is_incapacitated)}`}
                                                    style={{ width: `${character.health_percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleStartHealing(character.id)}
                                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors cursor-pointer"
                                    >
                                        Heal
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-amber-600/70 italic">
                            No characters available for healing. Characters must be damaged and not assigned to other buildings.
                        </p>
                    )}
                </div>
            )}

            {/* Level/Star effects table */}
            <div className="mt-4 space-y-3">
                <h4 className="text-sm font-medium text-amber-800/80">Apothecary Benefits by Level</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-amber-700/70">
                                <th className="text-left py-1 px-2">Level</th>
                                <th className="text-left py-1 px-2">Healing Time</th>
                                <th className="text-left py-1 px-2">Passive Bonus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => {
                                const healingTimes = { 1: 60, 2: 50, 3: 40, 4: 30, 5: 20 };
                                return (
                                    <tr
                                        key={lvl}
                                        className={`border-b border-amber-300/30 ${apothecaryInfo.level === lvl ? 'bg-green-200/30' : ''}`}
                                    >
                                        <td className="py-1.5 px-2 font-medium">Level {lvl}</td>
                                        <td className="py-1.5 px-2">{healingTimes[lvl as keyof typeof healingTimes]} min</td>
                                        <td className="py-1.5 px-2">+{lvl * 10} Body</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <h4 className="text-sm font-medium text-amber-800/80 mt-3">Capacity by Stars</h4>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-amber-700/70">
                                <th className="text-left py-1 px-2">Stars</th>
                                <th className="text-left py-1 px-2">Character Slots</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${apothecaryInfo.stars === s ? 'bg-blue-200/30' : ''}`}
                                >
                                    <td className="py-1.5 px-2">
                                        {'★'.repeat(s)}{'☆'.repeat(5-s)}
                                    </td>
                                    <td className="py-1.5 px-2">{s} character{s > 1 ? 's' : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}


/**
 * Chapel Management Info component
 * Shows faction alignment, donation form, and reputation bonuses.
 */
function ChapelManagementInfo({
    chapelInfo,
    hexId,
    buildingId,
    hexResources,
    isOwner,
    isGm,
}: {
    chapelInfo: NonNullable<Props['chapelInfo']>;
    hexId: number;
    buildingId: number;
    hexResources: { tea: number; wood: number; mystical: number; medicinal: number; edible: number };
    isOwner: boolean;
    isGm: boolean;
}) {
    // State for faction selection
    const [selectedEnclaveId, setSelectedEnclaveId] = useState<number | null>(null);
    const [isAligning, setIsAligning] = useState(false);
    const [isChanging, setIsChanging] = useState(false);
    const [showChangeConfirm, setShowChangeConfirm] = useState(false);

    // State for donation form
    const [donationResourceType, setDonationResourceType] = useState<string>('tea');
    const [donationAmount, setDonationAmount] = useState<number>(10);
    const [isDonating, setIsDonating] = useState(false);

    // Resource type options with labels
    const resourceTypes = [
        { key: 'tea', label: 'Tea', amount: hexResources.tea },
        { key: 'wood', label: 'Wood', amount: hexResources.wood },
        { key: 'mystical', label: 'Mystical', amount: hexResources.mystical },
        { key: 'medicinal', label: 'Medicinal', amount: hexResources.medicinal },
        { key: 'edible', label: 'Edible', amount: hexResources.edible },
    ];

    // Calculate reputation gain for current donation amount
    const calculateReputationGain = () => {
        if (donationAmount < chapelInfo.resources_per_reputation) return 0;
        return Math.floor(donationAmount / chapelInfo.resources_per_reputation) + chapelInfo.donation_reputation_bonus;
    };

    // Check if donation qualifies for stat bonus chance
    const qualifiesForStatBonus = donationAmount >= chapelInfo.large_donation_threshold;

    // Handle initial faction alignment
    const handleAlignFaction = () => {
        if (!selectedEnclaveId || isAligning) return;
        setIsAligning(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/chapel/align`,
            { enclave_id: selectedEnclaveId },
            {
                preserveScroll: true,
                onFinish: () => setIsAligning(false),
            }
        );
    };

    // Handle faction change (with penalty)
    const handleChangeFaction = () => {
        if (!selectedEnclaveId || isChanging) return;
        setIsChanging(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/chapel/change`,
            { enclave_id: selectedEnclaveId },
            {
                preserveScroll: true,
                onFinish: () => {
                    setIsChanging(false);
                    setShowChangeConfirm(false);
                },
            }
        );
    };

    // Handle resource donation
    const handleDonate = () => {
        if (isDonating || donationAmount < chapelInfo.resources_per_reputation) return;
        setIsDonating(true);
        router.post(
            `/hex/${hexId}/buildings/${buildingId}/chapel/donate`,
            {
                resource_type: donationResourceType,
                amount: donationAmount,
            },
            {
                preserveScroll: true,
                onFinish: () => setIsDonating(false),
            }
        );
    };

    // Get current selected resource amount
    const currentResourceAmount = resourceTypes.find(r => r.key === donationResourceType)?.amount ?? 0;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <span className="text-lg">⛪</span>
                <h3 className="font-['Cinzel'] text-lg text-amber-900/90">Chapel Management</h3>
            </div>

            {/* Current Status Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-200/30">
                    <div className="text-amber-700/60 text-xs uppercase tracking-wide mb-1">Reputation Bonus</div>
                    <div className="text-amber-900 font-medium">+{chapelInfo.reputation_bonus_percent}%</div>
                    <div className="text-xs text-amber-600/70 mt-1">
                        From adventures for aligned faction
                    </div>
                </div>
                <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-200/30">
                    <div className="text-amber-700/60 text-xs uppercase tracking-wide mb-1">Donation Rate</div>
                    <div className="text-amber-900 font-medium">
                        {chapelInfo.resources_per_reputation} resources = 1 rep
                        {chapelInfo.donation_reputation_bonus > 0 && (
                            <span className="text-green-600 ml-1">(+{chapelInfo.donation_reputation_bonus})</span>
                        )}
                    </div>
                    <div className="text-xs text-amber-600/70 mt-1">
                        {chapelInfo.stat_bonus_chance > 0 && (
                            <>{chapelInfo.stat_bonus_chance}% stat bonus on 100+ donations</>
                        )}
                    </div>
                </div>
            </div>

            {/* Aligned Faction Section */}
            <div className="space-y-2">
                <h4 className="text-sm font-medium text-amber-800/80 flex items-center gap-2">
                    <span>🏰</span> Aligned Faction
                </h4>

                {chapelInfo.aligned_faction_id ? (
                    // Currently aligned to a faction
                    <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-200/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-amber-900">{chapelInfo.aligned_faction_name}</div>
                                <div className="text-xs text-amber-600/70">
                                    +{chapelInfo.reputation_bonus_percent}% reputation bonus on adventures
                                </div>
                            </div>
                        </div>

                        {/* Faction Change Option */}
                        {isOwner && chapelInfo.can_change_faction && !showChangeConfirm && (
                            <button
                                onClick={() => setShowChangeConfirm(true)}
                                className="mt-2 text-xs text-amber-600 hover:text-amber-800 underline cursor-pointer"
                            >
                                Change Faction
                            </button>
                        )}

                        {/* Cooldown Warning */}
                        {!chapelInfo.can_change_faction && chapelInfo.cooldown_remaining_hours !== null && (
                            <div className="mt-2 text-xs text-amber-600/70">
                                ⏳ Can change faction in {chapelInfo.cooldown_remaining_hours} hours
                            </div>
                        )}
                    </div>
                ) : (
                    // No faction aligned yet
                    <div className="bg-amber-50/30 rounded-lg p-3 border border-amber-200/30">
                        <div className="text-amber-700/70 text-sm mb-2">
                            No faction aligned. Select a faction to receive reputation bonuses.
                        </div>
                    </div>
                )}
            </div>



            {/* Faction Selection / Change Form */}
            {isOwner && (
                (!chapelInfo.aligned_faction_id || showChangeConfirm) && chapelInfo.available_enclaves.length > 0 ? (
                    <div className="bg-white/40 rounded-lg p-3 border border-amber-200/30 space-y-3">
                        {/* Change Confirmation Warning */}
                        {showChangeConfirm && (
                            <div className="bg-red-50/50 rounded-lg p-2 border border-red-200/30 text-sm">
                                <div className="font-medium text-red-800 mb-1">⚠️ Faction Change Warning</div>
                                <div className="text-red-700/80 text-xs">
                                    Changing factions will cost <strong>{chapelInfo.faction_change_penalty} reputation</strong> with your current faction and trigger a 24-hour cooldown.
                                </div>
                            </div>
                        )}

                        {/* Faction Dropdown */}
                        <div>
                            <label className="text-xs text-amber-700/70 block mb-1">
                                {showChangeConfirm ? 'Select New Faction' : 'Select Faction to Align'}
                            </label>
                            <select
                                value={selectedEnclaveId ?? ''}
                                onChange={(e) => setSelectedEnclaveId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-3 py-2 rounded-lg border border-amber-200/50 bg-white/60 text-amber-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                            >
                                <option value="">-- Choose a Faction --</option>
                                {chapelInfo.available_enclaves.map((enclave) => (
                                    <option key={enclave.id} value={enclave.id}>
                                        {enclave.name}
                                        {enclave.owner_name && ` (${enclave.owner_name})`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                            {showChangeConfirm ? (
                                <>
                                    <button
                                        onClick={handleChangeFaction}
                                        disabled={!selectedEnclaveId || isChanging}
                                        className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                    >
                                        {isChanging ? 'Changing...' : 'Confirm Change'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowChangeConfirm(false);
                                            setSelectedEnclaveId(null);
                                        }}
                                        className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 cursor-pointer transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleAlignFaction}
                                    disabled={!selectedEnclaveId || isAligning}
                                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                >
                                    {isAligning ? 'Aligning...' : 'Align with Faction'}
                                </button>
                            )}
                        </div>
                    </div>
                ) : chapelInfo.available_enclaves.length === 0 && !chapelInfo.aligned_faction_id ? (
                    <div className="text-xs text-amber-600/70 italic">
                        No enclaves available to align with.
                    </div>
                ) : null
            )}

            {/* Resource Donation Section */}
            {chapelInfo.aligned_faction_id && isOwner && (
                <div className="space-y-2">
                    <h4 className="text-sm font-medium text-amber-800/80 flex items-center gap-2">
                        <span>🎁</span> Donate Resources
                        <span className="text-xs font-normal text-amber-600/70">
                            (gain reputation with aligned faction)
                        </span>
                    </h4>

                    <div className="bg-white/40 rounded-lg p-3 border border-amber-200/30 space-y-3">
                        {/* Resource Type Selection */}
                        <div>
                            <label className="text-xs text-amber-700/70 block mb-1">Resource Type</label>
                            <select
                                value={donationResourceType}
                                onChange={(e) => setDonationResourceType(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-amber-200/50 bg-white/60 text-amber-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                            >
                                {resourceTypes.map((resource) => (
                                    <option key={resource.key} value={resource.key}>
                                        {resource.label} (Available: {resource.amount})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Amount Input */}
                        <div>
                            <label className="text-xs text-amber-700/70 block mb-1">
                                Amount (min: {chapelInfo.resources_per_reputation}, max: {currentResourceAmount})
                            </label>
                            <input
                                type="number"
                                min={chapelInfo.resources_per_reputation}
                                max={currentResourceAmount}
                                value={donationAmount}
                                onChange={(e) => setDonationAmount(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-3 py-2 rounded-lg border border-amber-200/50 bg-white/60 text-amber-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 caret-gray-900"
                            />
                        </div>

                        {/* Donation Preview */}
                        <div className="bg-amber-50/50 rounded-lg p-2 border border-amber-200/30 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-amber-700/70">Reputation Gain:</span>
                                <span className="font-medium text-amber-900">+{calculateReputationGain()}</span>
                            </div>
                            {qualifiesForStatBonus && chapelInfo.stat_bonus_chance > 0 && (
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-amber-700/70">Stat Bonus Chance:</span>
                                    <span className="font-medium text-green-600">✨ {chapelInfo.stat_bonus_chance}%</span>
                                </div>
                            )}
                        </div>

                        {/* Donate Button */}
                        <button
                            onClick={handleDonate}
                            disabled={isDonating || donationAmount < chapelInfo.resources_per_reputation || donationAmount > currentResourceAmount}
                            className="w-full px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                        >
                            {isDonating ? 'Donating...' : `Donate ${donationAmount} ${donationResourceType}`}
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
}
